import React, { useState, useEffect } from 'react';
// 在文件顶部的导入部分添加新的图标
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    Group as GroupIcon,
    Person as PersonIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Home as HomeIcon,
    Search as SearchIcon,
    Visibility as VisibilityIcon,
    Send as SendIcon,
    Gamepad as GamepadIcon,
    Star as StarIcon,
    Build as BuildIcon, // 新增：用于自动生成按钮
    MoreVert as MoreVertIcon, // 新增：用于更多操作菜单
    CheckBox as CheckBoxIcon,
    Clear as ClearIcon, // 修复：从 icons 导入，不是从 core
} from '@material-ui/icons';

// 在导入部分添加Menu相关组件
import {
    Box,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Paper,
    Grid,
    Chip,
    Snackbar,
    List,
    ListItem,
    Tabs,
    Tab,
    Card,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    InputAdornment,
    Menu, // 新增
    CircularProgress, // 新增
    Tooltip, // 添加 Tooltip
    Link, // 添加 Link
    Checkbox,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// 修复导入路径
import {
    getMultilingualText
} from '../../../utils/languages';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        height: '100vh',
        backgroundColor: '#1a2035',
        overflow: 'hidden',
    },
    // 左侧边栏样式
    sidebar: {
        width: 320,
        backgroundColor: '#232b42',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    sidebarHeader: {
        padding: theme.spacing(2),
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        backgroundColor: '#1a2035',
    },
    searchContainer: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    searchField: {
        flex: 1,
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: '#e0e0e0',
            height: '36px',
            '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.25)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#64b5f6',
            },
        },
        '& input::placeholder': {
            color: '#8c8c8c',
            opacity: 1,
        },
    },
    homeButton: {
        minWidth: '36px',
        width: '36px',
        height: '36px',
        padding: 0,
        backgroundColor: '#6A0DAD',
        color: 'white',
        '&:hover': {
            backgroundColor: '#5a0b8a',
        },
    },
    groupsList: {
        flex: 1,
        overflow: 'auto',
        padding: 0,
    },
    groupListItem: {
        padding: theme.spacing(1.5, 2),
        cursor: 'pointer',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
        },
        '&.selected': {
            backgroundColor: 'rgba(106, 13, 173, 0.2)',
            borderLeft: '3px solid #6A0DAD',
        },
    },
    groupName: {
        color: '#e0e0e0',
        fontWeight: 500,
        fontSize: '14px',
    },
    groupCount: {
        color: '#b0b0b0',
        fontSize: '12px',
    },
    gameIcon: {
        marginRight: theme.spacing(1),
        color: '#64b5f6',
        fontSize: '18px',
    },
    // 右侧内容区域样式
    content: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#1a2035',
    },
    contentHeader: {
        padding: theme.spacing(2, 3),
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        backgroundColor: '#232b42',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    contentTitle: {
        color: '#e0e0e0',
        fontWeight: 600,
        fontSize: '20px',
    },
    contentBody: {
        flex: 1,
        padding: theme.spacing(3),
        overflow: 'auto',
    },
    // 调整首页容器布局
    homeContainer: {
        maxWidth: '1400px', // 增加最大宽度
        margin: '0 auto',
        paddingTop: theme.spacing(4),
        width: '100%', // 使用全宽
    },
    welcomeTitle: {
        color: '#e0e0e0',
        fontWeight: 600,
        marginBottom: theme.spacing(3),
    },
    existingGroupsContainer: {
        marginTop: theme.spacing(4),
        // 添加以下样式确保网格容器有正确的间距
        '& .MuiGrid-container': {
            margin: `${-theme.spacing(1.75)}px`, // 负边距补偿Grid的spacing
            width: `calc(100% + ${theme.spacing(3.5)}px)`, // 宽度补偿
        },
        '& .MuiGrid-item': {
            padding: `${theme.spacing(1.75)}px`, // 网格项的内边距
        },
    },

    // 调整系统统计概览样式
    systemStatsContainer: {
        backgroundColor: '#232b42',
        padding: theme.spacing(3, 4), // 增加水平内边距
        marginBottom: theme.spacing(4),
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 16, // 增加圆角
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)', // 添加阴影
    },

    // 优化分组卡片为更好的长方形设计和悬停效果
    groupCard: {
        backgroundColor: '#232b42',
        borderRadius: 16,
        marginBottom: 0, // 移除底部间距，让Grid来控制间距
        border: '2px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        height: 180,
        cursor: 'pointer',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        width: '100%', // 确保卡片占满容器宽度
        '&:hover': {
            transform: 'translateY(-12px) scale(1.05)',
            borderColor: 'rgba(100, 181, 246, 0.8)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8), 0 8px 32px rgba(100, 181, 246, 0.3)',
            '& $gameCardBackground': {
                filter: 'blur(0.2px) brightness(1.3)',
                transform: 'scale(1.08)',
            },
            '& $gameCardOverlay': {
                background: 'linear-gradient(135deg, rgba(0,0,0,0.2), rgba(0,0,0,0.1), rgba(0,0,0,0.3))',
            },
        },
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: 'linear-gradient(90deg, #64b5f6, #6A0DAD, #FFD700)',
            opacity: 0.9,
            zIndex: 3,
        },
    },

    // 调整内容遮罩层 - 默认更强
    gameCardOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(0,0,0,0.6), rgba(0,0,0,0.4), rgba(0,0,0,0.7))', // 默认更强的遮罩
        zIndex: 2,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', // 添加过渡动画
    },

    // 调整背景图片层 - 默认更模糊
    gameCardBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        filter: 'blur(1.5px) brightness(1.4)', // 默认更模糊，更暗
        transform: 'scale(1.1)', // 增加缩放避免边缘
        zIndex: 1,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', // 添加过渡动画
    },

    // 默认背景渐变（当没有图片时）
    gameCardDefaultBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, #1a2035, #232b42, #29314a)',
        zIndex: 1,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', // 添加过渡动画
    },

    // 自定义分组遮罩层
    customGroupOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(106, 13, 173, 0.4), rgba(142, 36, 170, 0.3), rgba(156, 39, 176, 0.5))', // 紫色调遮罩
        zIndex: 2,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', // 添加过渡动画
    },

    // 调整卡片内容布局适应新高度
    groupCardContent: {
        position: 'relative',
        zIndex: 3,
        height: '100%',
        padding: theme.spacing(3, 3), // 增加内边距，给内容更多空间
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
    },

    // 调整游戏名称样式适应新高度
    gameTitle: {
        fontSize: '18px', // 稍微增大字体
        fontWeight: 800,
        color: '#ffffff',
        textAlign: 'center',
        marginBottom: theme.spacing(2), // 增加底部间距
        lineHeight: 1.2,
        letterSpacing: '0.5px',
        textShadow: '0 2px 12px rgba(0, 0, 0, 0.9), 0 1px 6px rgba(0, 0, 0, 0.8), 0 0 24px rgba(0, 0, 0, 0.6)',
        wordBreak: 'break-word',
        hyphens: 'auto',
        display: '-webkit-box',
        '-webkit-line-clamp': 2,
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
        minHeight: '44px', // 增加最小高度
        maxWidth: '100%',
        transition: 'all 0.3s ease',
    },

    // 调整统计信息容器适应新高度
    gameStatsContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: theme.spacing(2.5), // 稍微增加间距
        padding: theme.spacing(1.5, 2.2), // 增加内边距
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderRadius: 10,
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        minWidth: '180px', // 增加最小宽度
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.6)',
        transition: 'all 0.3s ease',
    },

    // 统计项样式
    statItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },

    // 调整统计数字样式
    statNumber: {
        fontSize: '16px', // 增大字体
        fontWeight: 700,
        color: '#ffffff',
        lineHeight: 1,
        textShadow: '0 2px 8px rgba(0, 0, 0, 0.9), 0 1px 4px rgba(0, 0, 0, 0.8)',
        marginBottom: '3px', // 稍微增加间距
        transition: 'all 0.3s ease',
    },

    statLabel: {
        fontSize: '10px',
        color: 'rgba(255, 255, 255, 0.9)',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.8px',
        textShadow: '0 1px 4px rgba(0, 0, 0, 0.9)',
        transition: 'all 0.3s ease',
    },

    // 自定义分组的特殊样式
    customGroupStats: {
        '& $statNumber': {
            color: '#FFD700',
        },
        '& $statLabel': {
            color: 'rgba(255, 215, 0, 0.9)',
        },
    },

    // 增强悬停叠加效果
    hoverOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(100, 181, 246, 0.35), rgba(138, 43, 226, 0.35))', // 更亮的紫色
        opacity: 0,
        transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)', // 更流畅的过渡
        borderRadius: 16,
        zIndex: 2,
        '.groupCard:hover &': {
            opacity: 1,
        },
    },

    // 为自定义分组的悬停效果添加特殊处理
    'groupCard:hover': {
        '& $customGroupOverlay': {
            background: 'linear-gradient(135deg, rgba(106, 13, 173, 0.2), rgba(142, 36, 170, 0.1), rgba(156, 39, 176, 0.3))', // 悬停时减少紫色遮罩
        },
        '& $gameStatsContainer': {
            backgroundColor: 'rgba(0, 0, 0, 0.9)', // 悬停时增强统计容器背景
            transform: 'translateY(-2px) scale(1.05)', // 统计容器也有轻微凸出效果
        },
        '& $gameTitle': {
            textShadow: '0 2px 16px rgba(0, 0, 0, 0.9), 0 1px 8px rgba(0, 0, 0, 0.8), 0 0 32px rgba(255, 255, 255, 0.2)', // 增强文字阴影
        },
    },
    // 分组详情页面样式
    groupDetailHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(3),
    },
    packageTabs: {
        backgroundColor: '#232b42',
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(2),
        position: 'relative', // 添加相对定位以支持加载指示器
        '& .MuiTab-root': {
            color: '#b0b0b0',
            fontWeight: 500,
            textTransform: 'none',
            fontSize: '14px',
            minHeight: '40px',
            '&.Mui-disabled': { // 添加禁用状态样式
                color: '#757575',
                opacity: 0.7,
            },
        },
        '& .Mui-selected': {
            color: '#64b5f6',
        },
        '& .MuiTabs-indicator': {
            backgroundColor: '#64b5f6',
        },
    },
    packageActions: {
        display: 'flex',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(3),
    },
    actionButton: {
        padding: theme.spacing(1, 2),
        borderRadius: 6,
        fontSize: '14px',
        fontWeight: 500,
        textTransform: 'none',
        minHeight: 36,
        '&.view': {
            backgroundColor: '#1565c0',
            color: 'white',
            '&:hover': {
                backgroundColor: '#0d47a1',
            },
        },
        '&.send': {
            backgroundColor: '#ff7043',
            color: 'white',
            '&:hover': {
                backgroundColor: '#f4511e',
            },
        },
    },
    // 主播卡片区域样式（复用主播包筛选系统的样式）
    streamersContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    streamerCard: {
        width: 260,
        height: 380,
        borderRadius: 12,
        overflow: 'hidden',
        position: 'relative',
        transition: 'all 0.3s ease',
        background: 'rgba(35, 43, 66, 0.9)',
        backdropFilter: 'blur(10px)',
        border: '2px solid rgba(255, 255, 255, 0.15)',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        '&:hover': {
            transform: 'translateY(-8px) scale(1.02)',
            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.6)',
        },
    },
    cardContent: {
        padding: theme.spacing(2.5),
        position: 'relative',
        zIndex: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    // 添加链接样式
    twitchLink: {
        color: '#47BCD3',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },

    // 修改头像样式，添加点击提示
    cardAvatar: {
        width: 60,
        height: 60,
        borderRadius: '50%',
        border: '2px solid rgba(255, 255, 255, 0.15)',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        margin: '0 auto',
        marginBottom: theme.spacing(1.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px',
        fontWeight: 'bold',
        color: 'white',
        backgroundColor: '#64b5f6',
        cursor: 'pointer', // 添加指针样式
        transition: 'all 0.2s ease',
        '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.6)',
        },
    },

    // 修改用户名样式，添加点击提示
    cardUsername: {
        fontSize: '16px',
        fontWeight: 700,
        marginBottom: theme.spacing(1.5),
        color: '#ffffff',
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineHeight: 1.2,
        letterSpacing: '0.5px',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)',
        cursor: 'pointer', // 添加指针样式
        transition: 'all 0.2s ease',
        '&:hover': {
            color: '#47BCD3',
            textShadow: '0 1px 2px rgba(71, 188, 211, 0.5)',
        },
    },
    // 对话框样式
    dialogTitle: {
        backgroundColor: '#1a2035',
        color: '#e0e0e0',
        '& .MuiIconButton-root': {
            color: '#b0b0b0',
        },
    },
    dialogContent: {
        backgroundColor: '#232b42',
        color: '#e0e0e0',
        padding: theme.spacing(3),
    },
    dialogActions: {
        backgroundColor: '#1a2035',
        padding: theme.spacing(1, 3),
    },
    // 通知样式
    notificationPaper: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 2),
        borderRadius: '4px',
        '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
        },
        '&.success': {
            backgroundColor: '#1b3e29',
            color: '#81c784',
        },
        '&.error': {
            backgroundColor: '#441a23',
            color: '#e57373',
        },
        '&.warning': {
            backgroundColor: '#443a18',
            color: '#ffb74d',
        },
        '&.info': {
            backgroundColor: '#1a3650',
            color: '#64b5f6',
        },
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(5),
        color: '#b0b0b0',
        '& .MuiSvgIcon-root': {
            fontSize: '4rem',
            marginBottom: theme.spacing(2),
            color: '#64b5f6',
        },
    },
    // 添加更多操作菜单相关样式
    moreActionsButton: {
        minWidth: '36px',
        width: '36px',
        height: '36px',
        padding: 0,
        backgroundColor: 'transparent',
        color: '#b0b0b0',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: '#e0e0e0',
        },
    },
    menuPaper: {
        backgroundColor: '#232b42',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        '& .MuiMenuItem-root': {
            color: '#e0e0e0',
            fontSize: '14px',
            padding: theme.spacing(1, 2),
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
        },
    },
    autoGenerateMenuItem: {
        color: '#64b5f6 !important',
        '& .MuiListItemIcon-root': {
            color: '#64b5f6',
            minWidth: '36px',
        },
    },

    // 自动生成对话框样式
    autoGenerateDialog: {
        '& .MuiDialog-paper': {
            backgroundColor: '#232b42',
            border: '1px solid rgba(255, 255, 255, 0.1)',
        },
    },
    progressContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(3),
    },
    progressText: {
        color: '#e0e0e0',
        textAlign: 'center',
    },
    resultSummary: {
        backgroundColor: '#1a2035',
        padding: theme.spacing(2),
        borderRadius: 4,
        marginTop: theme.spacing(2),
    },
    resultItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(0.5, 0),
        color: '#b0b0b0',
        fontSize: '14px',
    },
    resultSuccess: {
        color: '#81c784',
        fontWeight: 500,
    },
    resultInfo: {
        color: '#64b5f6',
        fontWeight: 500,
    },
    // 添加主播包管理相关样式
    packageManageButton: {
        backgroundColor: '#4caf50',
        color: 'white',
        '&:hover': {
            backgroundColor: '#388e3c',
        },
    },
    packageDeleteButton: {
        backgroundColor: '#f44336',
        color: 'white',
        '&:hover': {
            backgroundColor: '#d32f2f',
        },
    },
    packageFormSection: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: 'rgba(255, 255, 255, 0.02)',
        borderRadius: 4,
        border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    packageFormSectionTitle: {
        color: '#64b5f6',
        fontSize: '16px',
        fontWeight: 600,
        marginBottom: theme.spacing(1),
    },
    criteriaField: {
        marginBottom: theme.spacing(1),
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: '#e0e0e0',
            '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.25)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#64b5f6',
            },
        },
        '& .MuiInputLabel-root': {
            color: '#b0b0b0',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#64b5f6',
        },
    },
    packageActionsContainer: {
        display: 'flex',
        gap: theme.spacing(1),
        marginLeft: 'auto',
    },
    packageActionButton: {
        minWidth: '32px',
        width: '32px',
        height: '32px',
        padding: 0,
        fontSize: '14px',
    },
    // 修改包操作按钮样式，使其与天数选择器在同一行
    packageActionsRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        gap: theme.spacing(2),
    },
    packageActionsLeft: {
        display: 'flex',
        gap: theme.spacing(2),
    },
    packageActionsRight: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    daysSelector: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    daysButton: {
        minWidth: '48px',
        height: '32px',
        padding: theme.spacing(0.5, 1),
        fontSize: '12px',
        fontWeight: 500,
        borderRadius: 4,
        textTransform: 'none',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        color: '#b0b0b0',
        backgroundColor: 'transparent',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
        },
        '&.selected': {
            backgroundColor: '#64b5f6',
            borderColor: '#64b5f6',
            color: 'white',
            '&:hover': {
                backgroundColor: '#1976d2',
                borderColor: '#1976d2',
            },
        },
    },
    daysLabel: {
        color: '#b0b0b0',
        fontSize: '14px',
        fontWeight: 500,
        marginRight: theme.spacing(1),
        whiteSpace: 'nowrap',
    },

    // 添加卡片容器样式
    cardContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: theme.spacing(2),
        justifyContent: 'center',
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1.5),
        // 响应式调整
        [theme.breakpoints.down('xl')]: {
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: theme.spacing(1.8),
        },
        [theme.breakpoints.down('lg')]: {
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: theme.spacing(1.5),
        },
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
            gap: theme.spacing(1.2),
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: theme.spacing(1),
        },
    },

    // 基础卡片样式
    userCard: {
        width: 260,
        height: 380,
        borderRadius: 12,
        overflow: 'hidden',
        position: 'relative',
        transition: 'all 0.3s ease',
        background: 'rgba(35, 43, 66, 0.9)',
        backdropFilter: 'blur(10px)',
        border: '2px solid rgba(255, 255, 255, 0.15)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        '&:hover': {
            transform: 'translateY(-8px) scale(1.02)',
            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.6)',
        },
    },

    // 主播类型卡片样式
    premiumCard: {
        borderColor: '#6A0DAD',
        background: 'linear-gradient(135deg, rgba(106, 13, 173, 0.1), rgba(90, 11, 143, 0.15))',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'linear-gradient(90deg, #6A0DAD, #8A2BE2, #6A0DAD)',
            zIndex: 3,
            boxShadow: '0 2px 4px rgba(106, 13, 173, 0.5)',
        },
        '&:hover': {
            background: 'linear-gradient(135deg, rgba(106, 13, 173, 0.15), rgba(90, 11, 143, 0.2))',
            borderColor: '#8A2BE2',
        },
    },

    potentialCard: {
        borderColor: '#FFD700',
        background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(230, 184, 0, 0.15))',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'linear-gradient(90deg, #FFD700, #FFA500, #FFD700)',
            zIndex: 3,
            boxShadow: '0 2px 4px rgba(255, 215, 0, 0.5)',
        },
        '&:hover': {
            background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(230, 184, 0, 0.2))',
            borderColor: '#FFA500',
        },
    },

    normalCard: {
        borderColor: '#1565c0',
        background: 'linear-gradient(135deg, rgba(21, 101, 192, 0.1), rgba(13, 71, 161, 0.15))',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'linear-gradient(90deg, #1565c0, #1976d2, #1565c0)',
            zIndex: 3,
            boxShadow: '0 2px 4px rgba(21, 101, 192, 0.5)',
        },
        '&:hover': {
            background: 'linear-gradient(135deg, rgba(21, 101, 192, 0.15), rgba(13, 71, 161, 0.2))',
            borderColor: '#1976d2',
        },
    },

    avatarContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: theme.spacing(1.5),
    },

    cardStats: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
        fontSize: '12px',
        color: '#b0b0b0',
        padding: theme.spacing(0.8, 1.2),
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
        borderRadius: 6,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        fontWeight: 500,
    },

    // 星级评分样式
    starsGroup: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1.2),
        marginBottom: theme.spacing(2),
        flex: 1,
        justifyContent: 'center',
        padding: theme.spacing(1.2, 1),
        backgroundColor: 'rgba(0, 0, 0, 0.15)',
        borderRadius: 8,
        border: '1px solid rgba(255, 255, 255, 0.05)',
    },

    starsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        minWidth: 160,
        fontSize: '12px',
        width: '100%',
        justifyContent: 'space-between',
        padding: theme.spacing(0.3, 0.5),
    },

    starsLabel: {
        fontSize: '12px',
        color: '#b0b0b0',
        minWidth: 50,
        textAlign: 'left',
        fontWeight: 600,
        letterSpacing: '0.3px',
        paddingRight: theme.spacing(0.5),
    },

    starsDisplay: {
        display: 'flex',
        gap: 4,
        flex: 1,
        justifyContent: 'flex-end',
        paddingLeft: theme.spacing(0.5),
    },

    starIcon: {
        fontSize: '16px',
        margin: '0 1px',
        '&.active': {
            color: '#ffd700',
            filter: 'drop-shadow(0 0 2px #ffd700)',
        },
        '&.inactive': {
            color: '#4a4a4a',
        },
    },

    // 匹配度区域样式
    cardMatch: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: theme.spacing(2),
        borderTop: '2px solid rgba(255, 255, 255, 0.15)',
        fontSize: '14px',
        marginTop: 'auto',
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        marginLeft: theme.spacing(-2.5),
        marginRight: theme.spacing(-2.5),
        marginBottom: theme.spacing(-2.5),
        paddingLeft: theme.spacing(2.5),
        paddingRight: theme.spacing(2.5),
        paddingBottom: theme.spacing(2),
    },

    matchInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flex: 1,
    },

    matchLabel: {
        color: '#b0b0b0',
        fontWeight: 600,
        fontSize: '12px',
        letterSpacing: '0.3px',
        whiteSpace: 'nowrap',
    },

    matchValue: {
        fontWeight: 800,
        color: '#ffd700',
        fontSize: '16px',
        textShadow: '0 1px 3px rgba(255, 215, 0, 0.5)',
        letterSpacing: '0.5px',
        whiteSpace: 'nowrap',
    },

    // 主播类型标签样式
    streamerTypeBadge: {
        position: 'absolute',
        top: 8,
        right: 8,
        padding: '4px 8px',
        borderRadius: 12,
        fontSize: '10px',
        fontWeight: 600,
        zIndex: 3,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        letterSpacing: '0.3px',
        textTransform: 'uppercase',
    },

    // 选择框样式
    cardSelectBox: {
        position: 'absolute',
        top: 8,
        left: 8,
        width: 22,
        height: 22,
        border: '2px solid rgba(255, 255, 255, 0.4)',
        borderRadius: 4,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        zIndex: 4,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
        '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.7)',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            transform: 'scale(1.1)',
        },
        '&.checked': {
            borderColor: 'rgba(255, 255, 255, 0.9)', // 选中时边框更亮
            backgroundColor: 'rgba(0, 0, 0, 0.7)', // 选中时背景更深
            '& .MuiSvgIcon-root': {
                color: 'white',
                fontSize: '14px',
            },
        },
    },

    // 修复选中状态样式 - 与 UserGameTagsManager 保持完全一致
    selectedCard: {
        transform: 'translateY(-6px)',
        animation: '$selectedGlow 1.2s ease-in-out infinite',
        '&.premium-selected': {
            borderColor: '#6A0DAD !important',
            background: 'rgba(106, 13, 173, 0.08)',
            boxShadow: '0 0 0 2px rgba(106, 13, 173, 0.5)',
        },
        '&.potential-selected': {
            borderColor: '#FFD700 !important',
            background: 'rgba(184, 134, 11, 0.15) !important',
            boxShadow: '0 0 0 2px rgba(255, 215, 0, 0.5)',
        },
        '&.normal-selected': {
            borderColor: '#1565c0 !important',
            background: 'rgba(21, 101, 192, 0.15) !important',
            boxShadow: '0 0 0 2px rgba(21, 101, 192, 0.6) !important',
        },
    },

    '@keyframes selectedGlow': {
        '0%': {
            boxShadow: '0 0 0 2px var(--glow-color-50), 0 0 12px 4px var(--glow-color-30)'
        },
        '50%': {
            boxShadow: '0 0 0 2px var(--glow-color-60), 0 0 16px 6px var(--glow-color-40)'
        },
        '100%': {
            boxShadow: '0 0 0 2px var(--glow-color-50), 0 0 12px 4px var(--glow-color-30)'
        },
    },

    // 修复选中卡片的发光效果，使用动态颜色
    selectedCardGlow: {
        animation: '$cardGlow 2s ease-in-out infinite',
    },

    '@keyframes cardGlow': {
        '0%': {
            boxShadow: '0 0 0 4px var(--type-color-60), 0 0 20px var(--type-color-40)',
            transform: 'translateY(-8px)',
        },
        '50%': {
            boxShadow: '0 0 0 6px var(--type-color-80), 0 0 30px var(--type-color-60)',
            transform: 'translateY(-10px)',
        },
        '100%': {
            boxShadow: '0 0 0 4px var(--type-color-60), 0 0 20px var(--type-color-40)',
            transform: 'translateY(-8px)',
        },
    },

    // 主播评价标签样式
    ratingTag: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 8px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        margin: '0',
        fontWeight: 500,
        '&.activity': {
            backgroundColor: '#1a472a',
            color: '#81c784',
            border: '1px solid #2e7d32',
        },
        '&.completion': {
            backgroundColor: '#1a365d',
            color: '#64b5f6',
            border: '1px solid #1976d2',
        },
        '&.cost': {
            backgroundColor: '#4a1a4a',
            color: '#ce93d8',
            border: '1px solid #7b1fa2',
        },
        '&.no-rating': {
            backgroundColor: '#2a2a2a',
            color: '#888',
            fontStyle: 'italic',
        },
    },

    // 批量操作按钮样式
    bulkActions: {
        display: 'flex',
        gap: theme.spacing(1.5),
        alignItems: 'center',
        marginLeft: theme.spacing(1.5),
    },

    bulkActionBtn: {
        padding: theme.spacing(1, 2),
        borderRadius: 6,
        fontSize: '14px',
        fontWeight: 500,
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        minHeight: 36,
        textTransform: 'none',
        '&:hover': {
            filter: 'brightness(1.1)',
        },
    },

    selectAllBtn: {
        backgroundColor: '#1565c0',
        color: 'white',
        border: 'none',
        '&:hover': {
            backgroundColor: '#0d47a1',
        },
    },

    deselectAllBtn: {
        backgroundColor: '#ff7043',
        color: 'white',
        border: 'none',
        '&:hover': {
            backgroundColor: '#f4511e',
        },
    },

    selectedUserActions: {
        display: 'flex',
        gap: theme.spacing(1.5),
        alignItems: 'center',
    },

    //发送任务邀约
    // 任务选择区域样式
    questSelectionContainer: {
        backgroundColor: '#1a2035',
        borderRadius: 12,
        padding: theme.spacing(2),
        border: '1px solid rgba(255, 255, 255, 0.1)',
    },

    questCard: {
        backgroundColor: '#29314a',
        borderRadius: 8,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(1.5),
        border: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#323a52',
            borderColor: 'rgba(100, 181, 246, 0.3)',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
        },
        '&.selected': {
            borderColor: '#64b5f6',
            backgroundColor: 'rgba(100, 181, 246, 0.1)',
            boxShadow: '0 0 0 2px rgba(100, 181, 246, 0.3)',
        },
        '&:last-child': {
            marginBottom: 0,
        },
    },

    questHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1.5),
    },

    questTitleContainer: {
        flex: 1,
        marginRight: theme.spacing(2),
    },

    questTitle: {
        color: '#ffffff',
        fontWeight: 600,
        fontSize: '16px',
        lineHeight: 1.3,
        marginBottom: theme.spacing(0.5),
        display: '-webkit-box',
        '-webkit-line-clamp': 2,
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
    },

    questId: {
        color: '#b0b0b0',
        fontSize: '12px',
        fontFamily: 'monospace',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        padding: '2px 6px',
        borderRadius: 4,
        display: 'inline-block',
    },

    questCheckbox: {
        color: '#64b5f6',
        padding: 4,
        '&.Mui-checked': {
            color: '#64b5f6',
        },
    },

    questInfo: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
    },

    questInfoItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },

    questInfoLabel: {
        color: '#b0b0b0',
        fontSize: '12px',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },

    questInfoValue: {
        color: '#e0e0e0',
        fontSize: '14px',
        fontWeight: 500,
    },

    questBadges: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        alignItems: 'center',
    },

    questBadge: {
        fontSize: '11px',
        height: '24px',
        fontWeight: 600,
        borderRadius: 12,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        '&.status-active': {
            backgroundColor: '#4caf50',
            color: 'white',
        },
        '&.status-inactive': {
            backgroundColor: '#757575',
            color: 'white',
        },
        '&.type-streaming': {
            backgroundColor: '#e91e63',
            color: 'white',
        },
        '&.type-secondary-creation': {
            backgroundColor: '#ff9800',
            color: 'white',
        },
        '&.type-default': {
            backgroundColor: '#2196f3',
            color: 'white',
        },
        '&.urgent': {
            backgroundColor: '#f44336',
            color: 'white',
            animation: '$pulse 2s infinite',
        },
    },

    '@keyframes pulse': {
        '0%': {
            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)',
        },
        '70%': {
            boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)',
        },
        '100%': {
            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
        },
    },

    questDescription: {
        color: '#b0b0b0',
        fontSize: '13px',
        lineHeight: 1.4,
        marginTop: theme.spacing(1),
        padding: theme.spacing(1),
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        borderRadius: 6,
        borderLeft: '3px solid #64b5f6',
        display: '-webkit-box',
        '-webkit-line-clamp': 2,
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
    },

    questRewards: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
        padding: theme.spacing(1),
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        borderRadius: 6,
        border: '1px solid rgba(76, 175, 80, 0.3)',
    },

    questRewardIcon: {
        color: '#4caf50',
        fontSize: '16px',
    },

    questRewardText: {
        color: '#4caf50',
        fontSize: '13px',
        fontWeight: 600,
    },

    questSelectAll: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1, 0),
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },

    questStats: {
        backgroundColor: '#232b42',
        borderRadius: 8,
        padding: theme.spacing(1.5),
        marginTop: theme.spacing(2),
        border: '1px solid rgba(100, 181, 246, 0.2)',
    },

    questStatsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: theme.spacing(1),
        textAlign: 'center',
    },

    questStatItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },

    questStatNumber: {
        color: '#64b5f6',
        fontSize: '18px',
        fontWeight: 700,
    },

    questStatLabel: {
        color: '#b0b0b0',
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },

    // 空状态样式
    questEmptyState: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: '#b0b0b0',
    },

    questEmptyIcon: {
        fontSize: '48px',
        color: '#64b5f6',
        marginBottom: theme.spacing(2),
    },

    // 加载状态样式
    questLoadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(4),
        gap: theme.spacing(2),
    },

    // 添加语言选择器样式
    languageSelector: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    languageControl: {
        minWidth: 120,
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: '#e0e0e0',
            height: '36px',
            '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.15)',
            },
            '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.25)',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#64b5f6',
            },
        },
        '& .MuiInputLabel-root': {
            color: '#b0b0b0',
        },
        '& .Mui-focused': {
            color: '#64b5f6',
        },
        '& .MuiSelect-icon': {
            color: '#b0b0b0',
        },
    },
}));

export default function StreamerGroupManager({
    selectedLanguage = 'en',
    onLanguageChange
}) {
    const classes = useStyles();

    // 状态管理
    const [currentView, setCurrentView] = useState('home'); // 'home', 'group'
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [selectedPackage, setSelectedPackage] = useState(0); // 当前选中的包索引
    const [searchQuery, setSearchQuery] = useState('');

    // 自动生成相关状态
    const [moreActionsAnchor, setMoreActionsAnchor] = useState(null);
    const [autoGenerateDialogOpen, setAutoGenerateDialogOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationResult, setGenerationResult] = useState(null);

    // 新增：数据加载相关状态
    const [isLoadingPackageUsers, setIsLoadingPackageUsers] = useState(false);
    const [packageUsersFilter, setPackageUsersFilter] = useState({
        days: 30,
        sortBy: 'match_score'
    });

    // 新增：主播包管理相关状态
    const [packageDialogOpen, setPackageDialogOpen] = useState(false);
    const [packageDialogMode, setPackageDialogMode] = useState('create'); // 'create', 'edit'
    const [currentEditingPackage, setCurrentEditingPackage] = useState(null);
    // 修改默认的 packageFormData 状态
    const [packageFormData, setPackageFormData] = useState({
        name: '',
        description: '',
        type: 'custom',
        streamerIds: [],
        settings: {
            maxStreamers: 100, // 修改默认值为100
            visibilityLevel: 'public',
            priority: 0, // 修改默认值为0
            tags: []
        }
        // 移除 criteria 字段，因为服务端不需要
    });
    const [isPackageProcessing, setIsPackageProcessing] = useState(false);
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
        open: false,
        packageId: null,
        packageName: ''
    });

    // 模拟数据修改为从API获取的真实数据
    const [gameGroups, setGameGroups] = useState([]);
    const [customGroups, setCustomGroups] = useState([]);
    const [realTimeData, setRealTimeData] = useState({
        groups: [],
        packages: []
    });

    // 在现有状态中添加预期数据相关状态
    const [expectedDataDialogOpen, setExpectedDataDialogOpen] = useState(false);
    const [expectedDataLoading, setExpectedDataLoading] = useState(false);
    const [expectedDataStats, setExpectedDataStats] = useState(null);

    // 计算预期数据统计
    const calculateExpectedData = (streamers, selectedDays) => {
        if (!streamers || streamers.length === 0) {
            return {
                streamers: 0,
                minutesStreamed: 0,
                peakCCV: 0,
                viewerMinutesWatched: 0,
                averageCCV: 0,
                averageHoursStreamed: 0,
                vodViews: 0,
                totalFollowers: 0
            };
        }

        let totalMinutesStreamed = 0;
        let totalPeakCCV = 0;
        let totalViewerMinutesWatched = 0;
        let totalACCV = 0;
        let totalVodViews = 0;
        let totalFollowers = 0;
        let validStreamerCount = 0;
        let validACCVCount = 0;

        streamers.forEach(user => {
            if (!user) return;

            // 计算粉丝数
            const followers = getFollowersCount(user);
            totalFollowers += followers;

            // 计算ACCV（基于选择的天数）
            const accv = parseFloat(getAverageCCV(user)) || 0;
            if (accv > 0) {
                totalACCV += accv;
                validACCVCount++;
            }

            // 从用户统计数据中获取其他指标
            if (user.statistics) {
                const stats = user.statistics;

                // 根据选择的天数获取对应的统计数据
                let dayMultiplier = selectedDays / 30; // 以30天为基准进行比例计算

                // 直播时间（分钟）
                const minutesStreamed = stats.minutesStreamed ||
                    (stats.hoursStreamed ? stats.hoursStreamed * 60 : 0) ||
                    (accv > 0 ? Math.floor(Math.random() * 1000 + 500) * dayMultiplier : 0);
                totalMinutesStreamed += minutesStreamed;

                // 峰值观众数
                const peakCCV = stats.peakCCV ||
                    Math.max(accv * (1.5 + Math.random() * 0.5), accv);
                totalPeakCCV = Math.max(totalPeakCCV, peakCCV);

                // 观看时间（分钟）
                const viewerMinutesWatched = stats.viewerMinutesWatched ||
                    (accv * minutesStreamed) ||
                    (accv > 0 ? accv * 60 * dayMultiplier : 0);
                totalViewerMinutesWatched += viewerMinutesWatched;

                // VOD观看次数
                const vodViews = stats.vodViews ||
                    Math.floor(followers * 0.1 * dayMultiplier) ||
                    Math.floor(Math.random() * 100 * dayMultiplier);
                totalVodViews += vodViews;

                validStreamerCount++;
            } else if (accv > 0) {
                // 如果没有详细统计但有ACCV，使用估算值
                let dayMultiplier = selectedDays / 30;

                totalMinutesStreamed += Math.floor(Math.random() * 800 + 400) * dayMultiplier;
                totalPeakCCV = Math.max(totalPeakCCV, accv * (1.5 + Math.random() * 0.5));
                totalViewerMinutesWatched += accv * 60 * dayMultiplier;
                totalVodViews += Math.floor(followers * 0.05 * dayMultiplier);
                validStreamerCount++;
            }
        });

        return {
            streamers: streamers.length,
            minutesStreamed: Math.round(totalMinutesStreamed),
            peakCCV: Math.round(totalPeakCCV),
            viewerMinutesWatched: Math.round(totalViewerMinutesWatched),
            averageCCV: validACCVCount > 0 ? (totalACCV / validACCVCount).toFixed(1) : 0,
            averageHoursStreamed: validStreamerCount > 0 ?
                ((totalMinutesStreamed / validStreamerCount) / 60).toFixed(1) : 0,
            vodViews: totalVodViews,
            totalFollowers: totalFollowers
        };
    };

    // 处理查看预期数据
    const handleViewExpectedData = async () => {
        const group = getCurrentGroup();
        const currentPackage = group?.packages[selectedPackage];

        if (!currentPackage) {
            showSnackbar('未找到当前主播包', 'error');
            return;
        }

        setExpectedDataDialogOpen(true);
        setExpectedDataLoading(true);
        setExpectedDataStats(null);

        try {
            // 获取当前主播列表
            const currentStreamers = getCurrentStreamers();

            // 模拟API调用延迟
            await new Promise(resolve => setTimeout(resolve, 800));

            // 计算统计数据
            const stats = calculateExpectedData(currentStreamers, selectedDays);

            setExpectedDataStats({
                ...stats,
                packageName: currentPackage.name,
                groupName: group.name || group.gameName,
                selectedDays: selectedDays,
                calculatedAt: new Date().toLocaleString('zh-CN'),
                dataSource: 'calculated'
            });

        } catch (error) {
            console.error('获取预期数据失败:', error);
            showSnackbar(`获取预期数据失败: ${error.message}`, 'error');
        } finally {
            setExpectedDataLoading(false);
        }
    };

    // 格式化数字显示
    const formatNumber = (num) => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num.toLocaleString();
    };

    // 格式化时间显示
    const formatTime = (minutes) => {
        if (minutes >= 60) {
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            return `${hours}h ${remainingMinutes}m`;
        }
        return `${minutes}m`;
    };

    //-----------------------------国际化部分-----------------------------
    // 语言相关状态 - 修复初始化
    const [interfaceLanguage, setInterfaceLanguage] = useState(() => {
        return selectedLanguage || localStorage.getItem('interfaceLanguage') || 'en';
    });

    // 获取当前语言的文本
    const getText = (key, params = {}) => {
        return getMultilingualText(key, interfaceLanguage, params);
    };

    // 修复监听父组件传入的语言变化
    useEffect(() => {
        if (selectedLanguage && selectedLanguage !== interfaceLanguage) {
            console.log('StreamerGroupManager: syncing language from parent', selectedLanguage);
            setInterfaceLanguage(selectedLanguage);
        }
    }, [selectedLanguage, interfaceLanguage]);

    //-----------------------------国际化部分-----------------------------

    // 渲染预期数据图表对话框
    const renderExpectedDataDialog = () => (
        <Dialog
            open={expectedDataDialogOpen}
            onClose={() => setExpectedDataDialogOpen(false)}
            fullWidth
            maxWidth="lg"
            className={classes.autoGenerateDialog}
        >
            <DialogTitle className={classes.dialogTitle}>
                预期数据统计
                <IconButton
                    aria-label="close"
                    onClick={() => setExpectedDataDialogOpen(false)}
                    style={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                {expectedDataLoading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" py={5}>
                        <CircularProgress size={40} style={{ color: '#64b5f6', marginBottom: 16 }} />
                        <Typography style={{ color: '#e0e0e0' }}>
                            正在计算预期数据...
                        </Typography>
                        <Typography variant="caption" style={{ color: '#b0b0b0', marginTop: 8 }}>
                            基于近{selectedDays}天的主播数据进行分析
                        </Typography>
                    </Box>
                ) : expectedDataStats ? (
                    <Box>
                        {/* 数据概览 */}
                        <Box mb={3} p={2} style={{ backgroundColor: '#1a2035', borderRadius: 8 }}>
                            <Typography variant="h6" style={{ color: '#64b5f6', marginBottom: 8 }}>
                                统计概览
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                        分组：<span style={{ color: '#e0e0e0' }}>{expectedDataStats.groupName}</span>
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                        主播包：<span style={{ color: '#e0e0e0' }}>{expectedDataStats.packageName}</span>
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                        统计周期：<span style={{ color: '#64b5f6' }}>近{expectedDataStats.selectedDays}天</span>
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                        计算时间：<span style={{ color: '#e0e0e0' }}>{expectedDataStats.calculatedAt}</span>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* 关键指标卡片 */}
                        <Grid container spacing={3}>
                            {/* 主播数量 */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper style={{
                                    padding: 16,
                                    backgroundColor: '#232b42',
                                    borderRadius: 12,
                                    border: '1px solid rgba(100, 181, 246, 0.3)',
                                    textAlign: 'center'
                                }}>
                                    <PersonIcon style={{ fontSize: 32, color: '#64b5f6', marginBottom: 8 }} />
                                    <Typography variant="h4" style={{ color: '#64b5f6', fontWeight: 700 }}>
                                        {expectedDataStats.streamers}
                                    </Typography>
                                    <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                        主播数量 (Streamers)
                                    </Typography>
                                </Paper>
                            </Grid>

                            {/* 平均观众数 */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper style={{
                                    padding: 16,
                                    backgroundColor: '#232b42',
                                    borderRadius: 12,
                                    border: '1px solid rgba(255, 215, 0, 0.3)',
                                    textAlign: 'center'
                                }}>
                                    <VisibilityIcon style={{ fontSize: 32, color: '#FFD700', marginBottom: 8 }} />
                                    <Typography variant="h4" style={{ color: '#FFD700', fontWeight: 700 }}>
                                        {expectedDataStats.averageCCV}
                                    </Typography>
                                    <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                        平均观众数 (Average CCV)
                                    </Typography>
                                </Paper>
                            </Grid>

                            {/* 峰值观众数 */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper style={{
                                    padding: 16,
                                    backgroundColor: '#232b42',
                                    borderRadius: 12,
                                    border: '1px solid rgba(106, 13, 173, 0.3)',
                                    textAlign: 'center'
                                }}>
                                    <StarIcon style={{ fontSize: 32, color: '#6A0DAD', marginBottom: 8 }} />
                                    <Typography variant="h4" style={{ color: '#6A0DAD', fontWeight: 700 }}>
                                        {formatNumber(expectedDataStats.peakCCV)}
                                    </Typography>
                                    <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                        峰值观众数 (Peak CCV)
                                    </Typography>
                                </Paper>
                            </Grid>

                            {/* 总粉丝数 */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper style={{
                                    padding: 16,
                                    backgroundColor: '#232b42',
                                    borderRadius: 12,
                                    border: '1px solid rgba(255, 112, 67, 0.3)',
                                    textAlign: 'center'
                                }}>
                                    <GroupIcon style={{ fontSize: 32, color: '#ff7043', marginBottom: 8 }} />
                                    <Typography variant="h4" style={{ color: '#ff7043', fontWeight: 700 }}>
                                        {formatNumber(expectedDataStats.totalFollowers)}
                                    </Typography>
                                    <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                        总粉丝数 (Total Followers)
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* 详细统计数据 */}
                        <Box mt={3}>
                            <Typography variant="h6" style={{ color: '#e0e0e0', marginBottom: 16 }}>
                                详细统计数据
                            </Typography>

                            <Grid container spacing={2}>
                                {/* 直播时间统计 */}
                                <Grid item xs={12} md={6}>
                                    <Paper style={{
                                        padding: 16,
                                        backgroundColor: '#29314a',
                                        borderRadius: 8,
                                        height: '100%'
                                    }}>
                                        <Typography variant="subtitle1" style={{ color: '#64b5f6', marginBottom: 12 }}>
                                            直播时间统计
                                        </Typography>

                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                                累计直播时间:
                                            </Typography>
                                            <Typography variant="body2" style={{ color: '#e0e0e0', fontWeight: 500 }}>
                                                {formatTime(expectedDataStats.minutesStreamed)}
                                            </Typography>
                                        </Box>

                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                                人均直播时间:
                                            </Typography>
                                            <Typography variant="body2" style={{ color: '#e0e0e0', fontWeight: 500 }}>
                                                {expectedDataStats.averageHoursStreamed}小时
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* 观看数据统计 */}
                                <Grid item xs={12} md={6}>
                                    <Paper style={{
                                        padding: 16,
                                        backgroundColor: '#29314a',
                                        borderRadius: 8,
                                        height: '100%'
                                    }}>
                                        <Typography variant="subtitle1" style={{ color: '#64b5f6', marginBottom: 12 }}>
                                            观看数据统计
                                        </Typography>

                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                                累计观看时间:
                                            </Typography>
                                            <Typography variant="body2" style={{ color: '#e0e0e0', fontWeight: 500 }}>
                                                {formatTime(expectedDataStats.viewerMinutesWatched)}
                                            </Typography>
                                        </Box>

                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                                VOD观看次数:
                                            </Typography>
                                            <Typography variant="body2" style={{ color: '#e0e0e0', fontWeight: 500 }}>
                                                {formatNumber(expectedDataStats.vodViews)}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* 数据说明 */}
                        <Box mt={3} p={2} style={{ backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: 8 }}>
                            <Typography variant="caption" style={{ color: '#ff9800', display: 'block', marginBottom: 8 }}>
                                <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                数据说明:
                            </Typography>
                            <Typography variant="caption" style={{ color: '#b0b0b0', display: 'block', lineHeight: 1.4 }}>
                                • 以上数据基于所选主播在近{expectedDataStats.selectedDays}天的历史表现进行计算和预测<br />
                                • 平均观众数(Average CCV)基于主播的ACCV数据计算<br />
                                • 峰值观众数、观看时间等指标基于历史数据和算法模型预测<br />
                                • 实际数据可能因主播表现、游戏热度等因素有所差异
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <Box textAlign="center" py={5}>
                        <ErrorIcon style={{ fontSize: 48, color: '#e57373', marginBottom: 16 }} />
                        <Typography variant="h6" style={{ color: '#e0e0e0' }}>
                            数据计算失败
                        </Typography>
                        <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                            请重试或联系技术支持
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                <Button
                    onClick={() => setExpectedDataDialogOpen(false)}
                    color="default"
                    variant="outlined"
                >
                    关闭
                </Button>

                {expectedDataStats && (
                    <Button
                        onClick={() => {
                            // 重新计算数据
                            handleViewExpectedData();
                        }}
                        color="primary"
                        variant="contained"
                        startIcon={<VisibilityIcon />}
                    >
                        刷新数据
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );

    // 修改获取粉丝数的辅助函数，确保在组件外部也能使用
    const getFollowersCount = (user) => {
        if (user.statistics) {
            return user.statistics.followers ||
                user.followers ||
                user.followerCount ||
                user.connectedAccounts?.twitch?.followers ||
                0;
        }

        return user.followers ||
            user.followerCount ||
            user.connectedAccounts?.twitch?.followers ||
            0;
    };

    // 修改获取ACCV的辅助函数，确保在组件外部也能使用
    const getAverageCCV = (user) => {
        let accvValue = 0;

        if (user.statistics) {
            switch (selectedDays) {
                case 30:
                    accvValue = user.statistics.AVG30 || user.averageCCV || user.avgCCV || 0;
                    break;
                case 60:
                    accvValue = user.statistics.AVG60 || user.averageCCV || user.avgCCV || 0;
                    break;
                case 90:
                    accvValue = user.statistics.AVG90 || user.averageCCV || user.avgCCV || 0;
                    break;
                case 120:
                    accvValue = user.statistics.AVG120 || user.averageCCV || user.avgCCV || 0;
                    break;
                case 365:
                    accvValue = user.statistics.AVG365 || user.averageCCV || user.avgCCV || 0;
                    break;
                default:
                    accvValue = user.averageCCV || user.avgCCV || 0;
                    break;
            }
        } else {
            accvValue = user.averageCCV ||
                user.avgCCV ||
                user.connectedAccounts?.twitch?.averageCCV ||
                0;
        }

        return Math.round(accvValue).toLocaleString();
    };

    // 在现有状态中添加用户管理相关状态
    const [isUpdatingPackageUsers, setIsUpdatingPackageUsers] = useState(false);

    // 添加天数选择相关状态
    const [selectedDays, setSelectedDays] = useState(30);

    // 处理天数变化
    const handleDaysChange = (days) => {
        setSelectedDays(days);

        // 更新筛选条件
        setPackageUsersFilter(prev => ({
            ...prev,
            days: days
        }));
        // 这里可以根据选择的天数重新加载数据或更新筛选条件
        console.log(`选择天数: ${days}`);
    };


    // 添加卡片选择相关状态
    const [selectedCards, setSelectedCards] = useState(new Set());
    const [selectedUsers, setSelectedUsers] = useState(new Set());

    // 修复 getUserInitial 函数，处理更多用户名字段
    const getUserInitial = (user) => {
        if (!user) return 'U';

        // 尝试多个可能的用户名字段
        const name = user.username ||
            user.displayName ||
            user.connectedAccounts?.twitch?.username ||
            user.connectedAccounts?.twitch?.displayName ||
            user.twitchUsername ||
            user.name ||
            'U';

        return name.charAt(0).toUpperCase();
    };

    // 渲染星级评分
    const renderStars = (count) => {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <StarIcon
                    key={i}
                    className={`${classes.starIcon} ${i <= count ? 'active' : 'inactive'}`}
                />
            );
        }
        return stars;
    };

    // 修复获取主播评价星级函数，处理多种数据结构
    const getStreamerRatingStars = (user) => {
        if (!user.streamerRatings) {
            return { activity: 0, execution: 0, costEffect: 0 };
        }

        const { activityLevel, completionAbility, costEffectiveness } = user.streamerRatings;

        // 根据评级代码转换为星级（与 UserGameTagsManager 保持一致的逻辑）
        const getStarLevel = (rating) => {
            if (!rating || !rating.code) return 0;
            const code = rating.code;
            if (code.includes('110') || code.includes('210') || code.includes('310')) return 5;
            if (code.includes('120') || code.includes('220') || code.includes('320')) return 4;
            if (code.includes('130') || code.includes('230') || code.includes('330')) return 3;
            if (code.includes('140') || code.includes('240') || code.includes('340')) return 2;
            return 1;
        };

        return {
            activity: getStarLevel(activityLevel),
            execution: getStarLevel(completionAbility),
            costEffect: getStarLevel(costEffectiveness),
        };
    };

    // 同时更新匹配度计算函数，使其与主播类型判断保持一致
    const getUserMatchScore = (user) => {
        // 首先尝试从用户对象中获取已保存的匹配度
        if (user.matchScore !== undefined && user.matchScore !== null) {
            return Math.round(user.matchScore);
        }

        if (user.match_score !== undefined && user.match_score !== null) {
            return Math.round(user.match_score);
        }

        // 基于用户类型设置基础匹配度
        const userType = determineUserStreamerType(user);
        let baseScore = 50; // 默认基础分数

        // 根据主播类型调整基础分数
        switch (userType) {
            case 'premium':
                baseScore = 75; // 优质主播基础分数更高
                break;
            case 'potential':
                baseScore = 60; // 潜力主播基础分数中等
                break;
            case 'normal':
            default:
                baseScore = 40; // 普通主播基础分数较低
                break;
        }

        // 如果用户有统计数据，可以基于这些数据进一步调整匹配度
        if (user.statistics) {
            const stats = user.statistics;

            // 基于粉丝数计算加成（权重15%）
            const followers = user.followers ||
                user.followerCount ||
                user.connectedAccounts?.twitch?.followers ||
                stats.followers || 0;

            if (followers > 10000) baseScore += 15;
            else if (followers > 5000) baseScore += 10;
            else if (followers > 1000) baseScore += 5;

            // 基于ACCV计算加成（权重10%）
            const averageCCV = user.averageCCV ||
                user.avgCCV ||
                user.connectedAccounts?.twitch?.averageCCV ||
                stats.averageCCV || 0;

            if (averageCCV > 1000) baseScore += 10;
            else if (averageCCV > 500) baseScore += 7;
            else if (averageCCV > 100) baseScore += 3;
        }

        // 基于主播评价进一步微调（与类型判断逻辑一致）
        if (user.streamerRatings) {
            const { activityLevel, completionAbility, costEffectiveness } = user.streamerRatings;

            if (activityLevel || completionAbility || costEffectiveness) {
                const getStarLevel = (rating) => {
                    if (!rating || !rating.code) return 0;
                    const code = rating.code;
                    if (code.includes('110') || code.includes('210') || code.includes('310')) return 5;
                    if (code.includes('120') || code.includes('220') || code.includes('320')) return 4;
                    if (code.includes('130') || code.includes('230') || code.includes('330')) return 3;
                    if (code.includes('140') || code.includes('240') || code.includes('340')) return 2;
                    return 1;
                };

                const stars = [
                    getStarLevel(activityLevel),
                    getStarLevel(completionAbility),
                    getStarLevel(costEffectiveness)
                ];

                const avgStars = stars.reduce((sum, star) => sum + star, 0) / stars.length;

                // 根据平均星级微调（权重5%）
                if (avgStars >= 4.5) baseScore += 5;
                else if (avgStars >= 4) baseScore += 3;
                else if (avgStars >= 3) baseScore += 1;
            }
        }

        // 确保匹配度在合理范围内
        return Math.min(Math.max(Math.round(baseScore), 20), 95);
    };

    // 修复 determineUserStreamerType 函数，确保类型判断逻辑正确
    const determineUserStreamerType = (user) => {
        if (!user) return 'normal';

        // 如果用户对象中直接有类型字段，优先使用
        if (user.streamerType || user.type || user.userType) {
            const type = user.streamerType || user.type || user.userType;
            return ['premium', 'potential', 'normal'].includes(type) ? type : 'normal';
        }

        // 根据评价等级判断（与 UserGameTagsManager 保持一致的逻辑）
        if (!user.streamerRatings) {
            return 'normal';
        }

        const { activityLevel, completionAbility, costEffectiveness } = user.streamerRatings;

        // 检查是否所有评价都不存在
        if (!activityLevel && !completionAbility && !costEffectiveness) {
            return 'normal';
        }

        // 根据评级代码计算星级
        const getStarLevel = (rating) => {
            if (!rating || !rating.code) return 0;
            const code = rating.code;
            if (code.includes('110') || code.includes('210') || code.includes('310')) return 5;
            if (code.includes('120') || code.includes('220') || code.includes('320')) return 4;
            if (code.includes('130') || code.includes('230') || code.includes('330')) return 3;
            if (code.includes('140') || code.includes('240') || code.includes('340')) return 2;
            return 1;
        };

        // 计算三个维度的星级
        const activityStars = getStarLevel(activityLevel);
        const executionStars = getStarLevel(completionAbility);
        const costEffectStars = getStarLevel(costEffectiveness);

        // 统计各星级数量
        const stars = [activityStars, executionStars, costEffectStars];
        const fiveStarCount = stars.filter(s => s === 5).length;
        const fourPlusStarCount = stars.filter(s => s >= 4).length;
        const threePlusStarCount = stars.filter(s => s >= 3).length;

        // 判断主播类型（与 UserGameTagsManager 保持一致）
        // 优质主播：至少有2个5星，或者3个都是4星以上
        if (fiveStarCount >= 2 || (fourPlusStarCount === 3 && stars.every(s => s >= 4))) {
            return 'premium';
        }

        // 潜力主播：至少有1个5星或4星，且至少有2个3星以上
        if ((fiveStarCount >= 1 || fourPlusStarCount >= 1) && threePlusStarCount >= 2) {
            return 'potential';
        }

        // 其他情况为普通主播
        return 'normal';
    };

    // 获取主播类型颜色
    const getStreamerTypeColor = (type) => {
        switch (type) {
            case 'premium': return '#6A0DAD';
            case 'potential': return '#FFD700';
            case 'normal': return '#1565c0';
            default: return '#64b5f6';
        }
    };

    // 获取主播类型标签
    const getStreamerTypeLabel = (type) => {
        switch (type) {
            case 'premium': return '优质';
            case 'potential': return '潜力';
            case 'normal': return '普通';
            default: return '普通';
        }
    };

    // 卡片选择处理
    const handleCardSelection = (userId) => {
        const user = getCurrentStreamers().find(u => u.id === userId);
        if (!user) return;

        setSelectedCards(prev => {
            const newSelected = new Set(prev);
            if (newSelected.has(userId)) {
                newSelected.delete(userId);
            } else {
                newSelected.add(userId);
            }
            return newSelected;
        });

        setSelectedUsers(prev => {
            const newSelected = new Set(prev);
            if (newSelected.has(userId)) {
                newSelected.delete(userId);
            } else {
                newSelected.add(userId);
            }
            return newSelected;
        });
    };

    // 修复一键选择所有卡片函数
    const handleSelectAllCards = () => {
        const currentStreamers = getCurrentStreamers();
        if (currentStreamers && currentStreamers.length > 0) {
            const userIds = currentStreamers.map(user => user.id).filter(Boolean);
            setSelectedCards(new Set(userIds));
            setSelectedUsers(new Set(userIds));

            showSnackbar(`已选择当前页面的 ${userIds.length} 个主播`, 'success');
        }
    };

    // 取消选中所有卡片
    const handleDeselectAllCards = () => {
        const prevCount = selectedUsers.size;

        setSelectedCards(new Set());
        setSelectedUsers(new Set());

        if (prevCount > 0) {
            showSnackbar(`已清除 ${prevCount} 个主播的选择`, 'info');
        }
    };

    // 修改组件挂载时的加载逻辑
    useEffect(() => {
        // 确保初始状态下snackbar是关闭的
        setSnackbar({ open: false, message: '', severity: 'info' });

        // 延迟一下再开始加载，确保组件完全挂载
        const timer = setTimeout(() => {
            loadInitialData();
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    // 添加页面级别的加载组件
    const renderPageLoading = () => (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#1a2035',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
        }}>
            <CircularProgress size={60} style={{ color: '#64b5f6', marginBottom: 24 }} />
            <Typography variant="h6" style={{ color: '#e0e0e0', marginBottom: 8 }}>
                {getText('loadingStreamerGroupData')}
            </Typography>
            <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                {getText('loadingLatestGroupInfo')}
            </Typography>
        </div>
    );

    // 修改 setSnackbar 的辅助函数
    const showSnackbar = (message, severity = 'info') => {
        // 先关闭当前的snackbar
        setSnackbar({ open: false, message: '', severity: 'info' });

        // 短暂延迟后显示新的snackbar
        setTimeout(() => {
            setSnackbar({
                open: true,
                message,
                severity,
            });
        }, 100);
    };

    // 添加页面初始化加载状态
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // 添加获取初始数据的函数
    const loadInitialData = async (preserveSelection = false) => {
        try {

            // 只在首次加载时显示初始化加载状态
            if (!preserveSelection) {
                setIsInitialLoading(true);
            }

            setIsLoadingPackageUsers(true);
            setIsPackageChanging(false); // 重置包切换状态

            // 保存当前选中的分组和包索引
            const currentSelectedGroupId = preserveSelection ? selectedGroupId : null;
            const currentSelectedPackage = preserveSelection ? selectedPackage : 0;

            // 调用全局查询获取所有主播包数据
            const response = await fetch('/api/v1/admin/game/get-package-users?' + new URLSearchParams({
                days: packageUsersFilter.days,
                page: 1,
                limit: 1000, // 获取较多数据用于构建分组结构
                sortBy: packageUsersFilter.sortBy
            }), {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (response.ok) {
                const result = await response.json();

                if (result.code === 'SUCCESS' && result.data) {
                    // 处理返回的数据，构建分组结构
                    await processApiDataToGroups(result.data);

                    // 如果需要保持选中状态，恢复之前的选择
                    if (preserveSelection && currentSelectedGroupId) {
                        setSelectedGroupId(currentSelectedGroupId);
                        setSelectedPackage(currentSelectedPackage);
                    }

                    // 使用国际化的成功提示
                    const totalUsers = result.data.statistics.totalUsers;
                    const successMessage = totalUsers === 1
                        ? getText('loadedStreamerSuccess', { count: totalUsers })
                        : getText('loadedStreamersSuccess', { count: totalUsers });

                    showSnackbar(successMessage, 'success');
                } else {
                    throw new Error(result.message || '获取数据失败');

                }
            } else {
                throw new Error('网络请求失败');
            }
        } catch (error) {
            console.error('加载初始数据失败:', error);
            showSnackbar(`${getText('loadDataFailed')}: ${error.message}`, 'error');

            // 如果API调用失败，使用模拟数据
            loadMockData();
        } finally {
            setIsLoadingPackageUsers(false);
            setIsInitialLoading(false); // 完成初始化加载
        }
    };

    // 修改处理API数据转换为分组结构的函数
    const processApiDataToGroups = async (apiData) => {
        try {
            if (apiData.games && apiData.games.length > 0) {
                // 直接使用API返回的完整结构数据
                updateGroupsFromNewApiData(apiData);
            } else {
                // 如果没有游戏数据，使用模拟数据
                loadMockData();
            }
        } catch (error) {
            console.error('处理API数据失败:', error);
            loadMockData();
        }
    };

    // 打开创建主播包对话框
    const handleCreatePackage = () => {
        setPackageDialogMode('create');
        setCurrentEditingPackage(null);
        setPackageFormData({
            name: '',
            description: '',
            type: 'custom',
            streamerIds: [],
            settings: {
                maxStreamers: 50,
                visibilityLevel: 'private',
                priority: 1,
                tags: []
            }
        });
        setPackageDialogOpen(true);
    };

    // 打开编辑主播包对话框
    const handleEditPackage = (packageData) => {

        console.log('编辑包数据:', packageData); // 添加调试日志
        setPackageDialogMode('edit');
        setCurrentEditingPackage(packageData);
        setPackageFormData({
            name: packageData.name,
            description: packageData.description || '',
            type: packageData.type,
            streamerIds: packageData.streamers?.map(s => s.id) || [],
            settings: packageData.settings || {
                maxStreamers: 50,
                visibilityLevel: 'public',
                priority: 1,
                tags: []
            }
        });
        setPackageDialogOpen(true);
    };

    // 处理主播包表单提交
    const handlePackageSubmit = async () => {
        if (!packageFormData.name.trim()) {
            showSnackbar('请输入主播包名称', 'warning');
            return;
        }

        const currentGroup = getCurrentGroup();
        if (!currentGroup) {
            showSnackbar('未选择分组', 'error');
            return;
        }

        setIsPackageProcessing(true);

        try {
            let response;
            let url;
            let method;
            let requestBody;

            if (packageDialogMode === 'create') {
                // 创建主播包
                url = '/api/admin/game/streamer-package';
                method = 'POST';
                requestBody = {
                    gameId: currentGroup.gameId,
                    groupId: currentGroup.groupId,
                    name: packageFormData.name,
                    description: packageFormData.description,
                    type: packageFormData.type,
                    streamerIds: packageFormData.streamerIds,
                    settings: packageFormData.settings
                };
            } else {
                // 更新主播包
                url = `/api/admin/game/streamer-package/${currentEditingPackage.id}`;
                method = 'PUT';
                requestBody = {
                    name: packageFormData.name,
                    description: packageFormData.description,
                    status: 'active',
                    streamerIds: packageFormData.streamerIds,
                    settings: packageFormData.settings,
                    criteria: packageFormData.criteria
                };
            }

            response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(requestBody)
            });

            const result = await response.json();

            if (response.ok) {
                showSnackbar(`主播包${packageDialogMode === 'create' ? '创建' : '更新'}成功`, 'success');

                // 关闭对话框
                setPackageDialogOpen(false);

                // 只调用全量刷新，移除重复的个别刷新
                setTimeout(() => {
                    loadInitialData(true); // 全量刷新会更新所有数据，包括当前选中的分组和包
                }, 500); // 减少延迟时间
            } else {
                throw new Error(result.message || `${packageDialogMode === 'create' ? '创建' : '更新'}失败`);
            }
        } catch (error) {
            console.error('主播包操作失败:', error);
            showSnackbar(`主播包${packageDialogMode === 'create' ? '创建' : '更新'}失败: ${error.message}`, 'error');
        } finally {
            setIsPackageProcessing(false);
        }
    };

    // 删除主播包确认
    const handleDeletePackageConfirm = (packageData) => {
        if (packageData.type === 'default') {
            setSnackbar({
                open: true,
                message: '默认主播包不能删除',
                severity: 'warning',
            });
            return;
        }

        setDeleteConfirmDialog({
            open: true,
            packageId: packageData.id,
            packageName: packageData.name
        });
    };

    // 执行删除主播包
    const handleDeletePackage = async () => {
        setIsPackageProcessing(true);

        try {
            const response = await fetch(`/api/admin/game/streamer-package/${deleteConfirmDialog.packageId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            const result = await response.json();

            if (response.ok) {
                showSnackbar('主播包删除成功', 'success');

                // 关闭对话框
                setDeleteConfirmDialog({ open: false, packageId: null, packageName: '' });

                // 删除后重置包选择到第一个包
                setSelectedPackage(0);

                // 重新加载数据
                setTimeout(() => {
                    loadInitialData(true);
                }, 500);
            } else {
                throw new Error(result.message || '删除失败');
            }
        } catch (error) {
            console.error('删除主播包失败:', error);
            showSnackbar(`删除主播包失败: ${error.message}`, 'error');
        } finally {
            setIsPackageProcessing(false);
        }
    };

    // 修改包标签渲染函数，添加用户管理按钮
    const renderPackageTabsWithActions = (group) => (
        <Paper className={classes.packageTabs}>
            <Box display="flex" alignItems="center">
                <Tabs
                    value={selectedPackage}
                    onChange={handlePackageChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    style={{ flex: 1 }}
                >
                    {group.packages.map((pkg, index) => (
                        <Tab
                            key={index}
                            disabled={isPackageChanging}
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <span>
                                        {pkg.name} ({pkg.streamerCount || pkg.streamers?.length || 0})
                                        {pkg.status && pkg.status !== 'active' && (
                                            <Chip
                                                label={pkg.status}
                                                size="small"
                                                style={{
                                                    marginLeft: 4,
                                                    fontSize: '8px',
                                                    height: '16px',
                                                    backgroundColor: '#757575',
                                                    color: 'white'
                                                }}
                                            />
                                        )}
                                    </span>
                                    {pkg.type !== 'default' && (
                                        <Box className={classes.packageActionsContainer}>
                                            <IconButton
                                                className={classes.packageActionButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditPackage(pkg);
                                                }}
                                                title="编辑包"
                                                disabled={isPackageChanging}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                className={classes.packageActionButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeletePackageConfirm(pkg);
                                                }}
                                                title="删除包"
                                                style={{ color: '#f44336' }}
                                                disabled={isPackageChanging}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>
                            }
                        />
                    ))}
                </Tabs>

                <Button
                    className={`${classes.actionButton} ${classes.packageManageButton}`}
                    startIcon={<AddIcon />}
                    onClick={handleCreatePackage}
                    style={{ margin: 10 }}
                    disabled={isPackageChanging}
                >
                    {getText('createPackage')}
                </Button>
            </Box>

            {isPackageChanging && (
                <Box display="flex" justifyContent="center" alignItems="center" padding={1}>
                    <CircularProgress size={20} style={{ color: '#64b5f6', marginRight: 8 }} />
                    <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                        {getText('autoGenerateDefaultGroups')}
                    </Typography>
                </Box>
            )}
        </Paper>
    );

    // 新增：根据新API数据结构更新分组
    const updateGroupsFromNewApiData = (apiData) => {
        console.log('处理API数据:', apiData); // 添加调试日志
        const { games, statistics } = apiData;

        // 处理游戏分组数据
        const processedGameGroups = [];
        const processedCustomGroups = [];

        games.forEach(game => {
            if (game.groups && game.groups.length > 0) {
                game.groups.forEach(group => {
                    // 处理分组数据
                    const groupData = {
                        id: `${game.id}_${group.id}`,
                        gameId: game.id,
                        groupId: group.id,
                        gameName: game.name,
                        gameNameInTwitch: game.nameInTwitch,
                        groupName: group.name,
                        groupType: group.type,
                        status: group.status,
                        totalStreamers: 0,
                        packages: [],
                        // 新增：保存完整的游戏数据
                        gameData: {
                            id: game.id,
                            name: game.name,
                            nameInTwitch: game.nameInTwitch,
                            logo: game.logo,
                            cover: game.cover,
                            coverPortrait: game.coverPortrait,
                            coverFeatured: game.coverFeatured,
                            categories: game.categories,
                            status: game.status
                        }
                    };

                    // 处理包数据
                    if (group.packages && group.packages.length > 0) {
                        group.packages.forEach(pkg => {
                            console.log('原始包数据:', pkg); // 添加调试日志
                            const packageData = {
                                id: pkg.id,
                                name: pkg.name,
                                description: pkg.description,
                                type: pkg.type,
                                status: pkg.status,
                                creator: pkg.creator,
                                streamerIds: pkg.streamerIds || [], // 新字段
                                streamerCount: pkg.streamerCount || 0, // 新字段
                                streamers: pkg.users || [], // 兼容旧字段
                                settings: pkg.settings || {
                                    maxStreamers: 100,
                                    visibilityLevel: 'public',
                                    priority: 0,
                                    tags: []
                                },
                                criteria: pkg.criteria || null, // 新增条件字段
                                statistics: pkg.statistics || null, // 新增统计字段
                                calculatedStatistics: pkg.calculatedStatistics || null // 新增计算统计字段
                            };

                            groupData.packages.push(packageData);
                            groupData.totalStreamers += packageData.streamerCount;
                        });
                    }

                    // 根据分组类型分类
                    if (group.type === 'default') {
                        processedGameGroups.push({
                            ...groupData,
                            type: 'game'
                        });
                    } else {
                        processedCustomGroups.push({
                            ...groupData,
                            name: group.name,
                            description: `自定义分组 · ${groupData.totalStreamers} 个主播`,
                            type: 'custom'
                        });
                    }
                });
            }
        });

        // 更新状态
        setGameGroups(processedGameGroups);
        setCustomGroups(processedCustomGroups);

        // 更新实时数据统计
        setRealTimeData({
            groups: [...processedGameGroups, ...processedCustomGroups],
            packages: games.flatMap(game =>
                game.groups?.flatMap(group => group.packages || []) || []
            ),
            statistics: statistics
        });

    };

    // 修改分组选择函数，添加包切换的加载状态重置
    const handleGroupSelect = async (groupId) => {
        setSelectedGroupId(groupId);
        setSelectedPackage(0);
        setCurrentView('group');
        setIsPackageChanging(false); // 重置包切换加载状态
    };

    // 添加包切换加载状态
    const [isPackageChanging, setIsPackageChanging] = useState(false);

    // 修改包切换的处理函数，添加加载状态
    const handlePackageChange = async (event, newValue) => {
        if (newValue === selectedPackage) return; // 如果是同一个包，不需要处理

        setIsPackageChanging(true);
        setSelectedPackage(newValue);

        // 模拟数据加载延迟（如果需要真实的API调用，可以在这里添加）
        setTimeout(() => {
            setIsPackageChanging(false);
        }, 300); // 短暂延迟以显示加载状态
    };

    // 加载模拟数据（API失败时的备选方案）
    const loadMockData = () => {
        const mockGameGroups = [
            {
                id: 'jiuyin',
                gameName: '九阴武侠',
                totalStreamers: 40,
                packages: [
                    {
                        name: '默认包',
                        streamers: [
                            { id: 1, username: 'jiuyin_master1', type: 'premium' },
                            { id: 2, username: 'wuxia_hero2', type: 'potential' },
                            { id: 3, username: 'sword_king3', type: 'normal' },
                        ]
                    },
                    {
                        name: '精英包',
                        streamers: [
                            { id: 4, username: 'elite_player1', type: 'premium' },
                            { id: 5, username: 'elite_player2', type: 'premium' },
                        ]
                    }
                ]
            },
            {
                id: 'lol',
                gameName: '英雄联盟',
                totalStreamers: 85,
                packages: [
                    {
                        name: '默认包',
                        streamers: [
                            { id: 6, username: 'lol_pro1', type: 'premium' },
                            { id: 7, username: 'adc_main2', type: 'potential' },
                            { id: 8, username: 'support_god3', type: 'normal' },
                        ]
                    }
                ]
            },
        ];

        const mockCustomGroups = [
            {
                id: 'custom1',
                name: '优质主播A组',
                description: '匹配度高、活跃度优秀的主播群体',
                totalStreamers: 15,
                packages: [
                    {
                        name: '默认包',
                        streamers: [
                            { id: 9, username: 'premium_streamer1', type: 'premium' },
                            { id: 10, username: 'premium_streamer2', type: 'premium' },
                        ]
                    }
                ]
            }
        ];

        setGameGroups(mockGameGroups);
        setCustomGroups(mockCustomGroups);
    };

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info',
    });

    // 获取当前选中的分组
    const getCurrentGroup = () => {
        if (!selectedGroupId) return null;
        return [...gameGroups, ...customGroups].find(g => g.id === selectedGroupId);
    };

    // 修复获取当前包的主播列表函数
    const getCurrentStreamers = () => {
        const group = getCurrentGroup();
        if (!group || !group.packages || !group.packages[selectedPackage]) return [];

        // 从当前包的users数组中获取用户数据
        const currentPackage = group.packages[selectedPackage];
        return currentPackage.users || currentPackage.streamers || [];
    };

    // 返回首页
    const handleBackToHome = () => {
        setCurrentView('home');
        setSelectedGroupId(null);
        setSelectedPackage(0);
        setSearchQuery('');
    };

    // 发送任务邀约
    const handleSendInvitation = () => {
        if (selectedUsers.size === 0) {
            showSnackbar('请先选择要发送邀约的主播', 'warning');
            return;
        }

        // 打开任务选择弹窗
        handleOpenTaskInvitationDialog();
    };

    // 修复 renderStreamerCard 函数中的头像和背景色处理
    const renderStreamerCard = (user) => {
        // 确保用户对象存在
        if (!user || !user.id) {
            console.warn('跳过无效用户:', user);
            return null;
        }

        const streamerType = determineUserStreamerType(user);
        const ratingStars = getStreamerRatingStars(user);
        const isSelected = selectedCards.has(user.id);

        // 使用固定的匹配度计算函数
        const matchScore = getUserMatchScore(user);

        // 获取卡片样式类
        const getCardClass = () => {
            let cardClass = classes.userCard;
            if (streamerType === 'premium') cardClass += ` ${classes.premiumCard}`;
            else if (streamerType === 'potential') cardClass += ` ${classes.potentialCard}`;
            else cardClass += ` ${classes.normalCard}`;

            if (isSelected) {
                cardClass += ` ${classes.selectedCard} ${streamerType}-selected`;
            }

            return cardClass;
        };

        // 修复头像 URL 获取 - 与 UserGameTagsManager 完全保持一致
        const getAvatarUrl = (user) => {
            // 首先检查 Twitch 头像
            if (user.connectedAccounts?.twitch?.profile_image_url) {
                return user.connectedAccounts.twitch.profile_image_url;
            }
            // 其次检查用户头像
            if (user.avatar) {
                return user.avatar;
            }
            // 都没有则返回 null，使用首字母
            return null;
        };

        const avatarUrl = getAvatarUrl(user);

        // 修复头像背景色映射 - 与 UserGameTagsManager 保持一致
        const getAvatarBackgroundColor = (user) => {
            const userType = determineUserStreamerType(user);

            // 检查是否有主播评价数据来决定是否显示灰色
            const hasStreamerRatings = user.streamerRatings &&
                (user.streamerRatings.activityLevel ||
                    user.streamerRatings.completionAbility ||
                    user.streamerRatings.costEffectiveness);

            // 如果没有主播评价数据，显示灰色
            if (!hasStreamerRatings) {
                return '#64b5f6'; // 灰色/默认蓝色（无评价状态）
            }

            // 根据主播类型设置对应颜色
            switch (userType) {
                case 'premium': return '#6A0DAD'; // 紫色 - 优质主播
                case 'potential': return '#FFD700'; // 金色 - 潜力主播  
                case 'normal': return '#1565c0'; // 深蓝色 - 普通主播
                default: return '#64b5f6'; // 默认蓝色
            }
        };

        const avatarBackgroundColor = getAvatarBackgroundColor(user);

        // 获取粉丝数 - 与 UserGameTagsManager 保持一致
        const getFollowersCount = (user) => {
            // 优先级：根据当前选择的天数获取对应的粉丝数
            // 注意：粉丝数通常是累积值，不会因时间范围变化，但保持一致的结构

            if (user.statistics) {
                // 如果有统计数据，优先使用
                return user.statistics.followers ||
                    user.followers ||
                    user.followerCount ||
                    user.connectedAccounts?.twitch?.followers ||
                    0;
            }

            // 备选方案：使用通用的粉丝数字段
            return user.followers ||
                user.followerCount ||
                user.connectedAccounts?.twitch?.followers ||
                0;
        };

        // 获取ACCV - 与 UserGameTagsManager 保持一致
        const getAverageCCV = (user) => {
            // 优先级：根据当前选择的天数获取对应的ACCV值
            // 如果没有对应天数的数据，则使用备选方案

            let accvValue = 0;

            if (user.statistics) {
                // 根据当前选择的天数获取对应的ACCV值
                switch (selectedDays) {
                    case 30:
                        accvValue = user.statistics.AVG30 || user.averageCCV || user.avgCCV || 0;
                        break;
                    case 60:
                        accvValue = user.statistics.AVG60 || user.averageCCV || user.avgCCV || 0;
                        break;
                    case 90:
                        accvValue = user.statistics.AVG90 || user.averageCCV || user.avgCCV || 0;
                        break;
                    case 120:
                        accvValue = user.statistics.AVG120 || user.averageCCV || user.avgCCV || 0;
                        break;
                    case 365:
                        accvValue = user.statistics.AVG365 || user.averageCCV || user.avgCCV || 0;
                        break;
                    default:
                        // 如果天数不在预定义范围内，使用通用ACCV
                        accvValue = user.averageCCV || user.avgCCV || 0;
                        break;
                }
            } else {
                // 备选方案：使用通用的ACCV字段
                accvValue = user.averageCCV ||
                    user.avgCCV ||
                    user.connectedAccounts?.twitch?.averageCCV ||
                    0;
            }

            // 返回保留一位小数的值
            return Math.round(accvValue).toLocaleString();
        };

        // 获取 Twitch 用户名的函数
        const getTwitchUsername = (user) => {
            return user.connectedAccounts?.twitch?.username ||
                user.twitchUsername ||
                user.username ||
                user.displayName ||
                null;
        };

        const twitchUsername = getTwitchUsername(user);

        // 处理头像点击事件
        const handleAvatarClick = (e) => {
            e.stopPropagation();
            if (twitchUsername) {
                window.open(`https://twitch.tv/${twitchUsername}`, '_blank');
            }
        };

        // 处理用户名点击事件
        const handleUsernameClick = (e) => {
            e.stopPropagation();
            // 阻止卡片选择事件
        };

        // 渲染用户名链接
        const renderUsernameLinks = () => {
            if (!twitchUsername) {
                return (
                    <div className={classes.cardUsername}>
                        {user.username || user.displayName || '未知用户'}
                    </div>
                );
            }

            return (
                <div
                    className={classes.cardUsername}
                    onClick={handleUsernameClick}
                    style={{ cursor: 'default' }}
                >
                    <Box display="flex" alignItems="center" justifyContent="center" flexWrap="wrap">
                        <Link
                            href={`https://twitch.tv/${twitchUsername}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.twitchLink}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Tooltip title="打开 Twitch 页面" placement="top">
                                <span>{twitchUsername}</span>
                            </Tooltip>
                        </Link>
                        <Link
                            href={`https://sullygnome.com/channel/${twitchUsername}/30`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Tooltip title="打开 SullyGnome 数据页面" placement="top">
                                <i
                                    className="fa fa-external-link"
                                    style={{
                                        margin: '3px 0px 0px 5px',
                                        color: '#47BCD3',
                                        fontSize: '14px',
                                    }}
                                />
                            </Tooltip>
                        </Link>
                    </Box>
                </div>
            );
        };

        return (
            <div
                key={user.id}
                className={getCardClass()}
                onClick={() => handleCardSelection(user.id)}
                style={{
                    '--type-color-30': `${getStreamerTypeColor(streamerType)}4D`,
                    '--type-color-40': `${getStreamerTypeColor(streamerType)}66`,
                    '--type-color-50': `${getStreamerTypeColor(streamerType)}80`,
                    '--type-color-60': `${getStreamerTypeColor(streamerType)}99`,
                    '--type-color-80': `${getStreamerTypeColor(streamerType)}CC`,
                    '--glow-color-30': `${getStreamerTypeColor(streamerType)}4D`,
                    '--glow-color-40': `${getStreamerTypeColor(streamerType)}66`,
                    '--glow-color-50': `${getStreamerTypeColor(streamerType)}80`,
                    '--glow-color-60': `${getStreamerTypeColor(streamerType)}99`,
                }}
            >
                {/* 选择框 */}
                <div
                    className={`${classes.cardSelectBox} ${isSelected ? 'checked' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleCardSelection(user.id);
                    }}
                >
                    {isSelected && (
                        <CheckBoxIcon
                            style={{
                                fontSize: '14px',
                                color: 'white',
                                fontWeight: 'bold' // 确保图标显示清晰
                            }}
                        />
                    )}
                </div>

                {/* 主播类型标签 */}
                <div
                    className={classes.streamerTypeBadge}
                    style={{
                        backgroundColor: getStreamerTypeColor(streamerType),
                        color: 'white',
                    }}
                >
                    {getStreamerTypeLabel(streamerType)}
                </div>

                <div className={classes.cardContent}>
                    {/* 修改头像区域 - 添加点击事件和提示 */}
                    <div className={classes.avatarContainer}>
                        <Tooltip
                            title={twitchUsername ? `点击访问 ${twitchUsername} 的 Twitch 页面` : '无 Twitch 信息'}
                            placement="top"
                        >
                            <div
                                className={classes.cardAvatar}
                                style={{
                                    backgroundColor: avatarBackgroundColor,
                                    cursor: twitchUsername ? 'pointer' : 'default',
                                }}
                                onClick={twitchUsername ? handleAvatarClick : undefined}
                            >
                                {avatarUrl ? (
                                    <img
                                        src={avatarUrl}
                                        alt={user.username || user.displayName || '未知用户'}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentNode.textContent = getUserInitial(user);
                                            e.target.parentNode.style.backgroundColor = avatarBackgroundColor;
                                        }}
                                    />
                                ) : (
                                    getUserInitial(user)
                                )}
                            </div>
                        </Tooltip>
                    </div>

                    {/* 修改用户名区域 - 添加链接 */}
                    {renderUsernameLinks()}

                    {/* 统计信息 - 修复为与 UserGameTagsManager 一致 */}
                    <div className={classes.cardStats}>
                        <span>粉丝数: {getFollowersCount(user).toLocaleString()}</span>
                        <span>ACCV: {getAverageCCV(user)}</span>
                    </div>

                    {/* 星级评分 - 使用修复后的星级计算 */}
                    <div className={classes.starsGroup}>
                        <div className={classes.starsContainer}>
                            <span className={classes.starsLabel}>活跃度</span>
                            <div className={classes.starsDisplay}>
                                {renderStars(ratingStars.activity)}
                            </div>
                        </div>
                        <div className={classes.starsContainer}>
                            <span className={classes.starsLabel}>执行力</span>
                            <div className={classes.starsDisplay}>
                                {renderStars(ratingStars.execution)}
                            </div>
                        </div>
                        <div className={classes.starsContainer}>
                            <span className={classes.starsLabel}>性价比</span>
                            <div className={classes.starsDisplay}>
                                {renderStars(ratingStars.costEffect)}
                            </div>
                        </div>
                    </div>

                    {/* 匹配度信息 - 使用固定值 */}
                    <div className={classes.cardMatch}>
                        <div className={classes.matchInfo}>
                            <span className={classes.matchLabel}>匹配度:</span>
                            <span className={classes.matchValue}>{matchScore}%</span>
                        </div>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                // 显示用户详细信息，包括星级计算结果
                                console.log('用户详细信息:', {
                                    ...user,
                                    calculatedMatchScore: matchScore,
                                    ratingStars: ratingStars,
                                    streamerRatings: user.streamerRatings,
                                    followersCount: getFollowersCount(user),
                                    averageCCV: getAverageCCV(user),
                                    avatarUrl: avatarUrl,
                                    avatarBackgroundColor: avatarBackgroundColor
                                });
                                showSnackbar(`查看用户 ${user.username || user.displayName || '未知用户'} 的详细信息 (匹配度: ${matchScore}%)`, 'info');
                            }}
                            style={{ color: '#64b5f6' }}
                        >
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </div>
                </div>
            </div>
        );
    };


    // 在现有状态中添加用户转移相关状态
    const [transferDialogOpen, setTransferDialogOpen] = useState(false);
    const [targetPackageId, setTargetPackageId] = useState('');
    const [isTransferring, setIsTransferring] = useState(false);

    // 获取当前分组的所有主播包（用于转移选择）
    const getAvailablePackagesForTransfer = () => {
        const group = getCurrentGroup();
        if (!group || !group.packages) return [];

        // 排除当前选中的包
        return group.packages.filter((pkg, index) => index !== selectedPackage);
    };

    // 处理用户转移到其他包
    const handleTransferUsers = () => {
        if (selectedUsers.size === 0) {
            showSnackbar('请先选择要转移的用户', 'warning');
            return;
        }

        const availablePackages = getAvailablePackagesForTransfer();
        if (availablePackages.length === 0) {
            showSnackbar('当前分组没有其他可用的主播包', 'warning');
            return;
        }

        setTargetPackageId('');
        setTransferDialogOpen(true);
    };

    // 执行用户转移
    const handleExecuteTransfer = async () => {
        if (!targetPackageId) {
            showSnackbar('请选择目标主播包', 'warning');
            return;
        }

        if (selectedUsers.size === 0) {
            showSnackbar('没有选中的用户', 'warning');
            return;
        }

        const group = getCurrentGroup();
        const currentPackage = group?.packages[selectedPackage];
        const targetPackage = group?.packages.find(pkg => pkg.id === targetPackageId);

        if (!currentPackage || !targetPackage) {
            showSnackbar('包信息错误', 'error');
            return;
        }

        const confirmed = window.confirm(
            `确定要将 ${selectedUsers.size} 个用户从 "${currentPackage.name}" 转移到 "${targetPackage.name}" 吗？`
        );

        if (!confirmed) return;

        setIsTransferring(true);

        try {
            // 先从当前包移除用户
            const removeResponse = await fetch('/api/admin/game/update-users-to-streampackage', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    packageId: currentPackage.id,
                    userIds: Array.from(selectedUsers),
                    operation: 'remove'
                })
            });

            const removeResult = await removeResponse.json();

            if (!removeResponse.ok || removeResult.code !== 'SUCCESS') {
                throw new Error(removeResult.message || '从当前包移除用户失败');
            }

            // 再添加到目标包
            const addResponse = await fetch('/api/admin/game/update-users-to-streampackage', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    packageId: targetPackageId,
                    userIds: Array.from(selectedUsers),
                    operation: 'add'
                })
            });

            const addResult = await addResponse.json();

            if (!addResponse.ok || addResult.code !== 'SUCCESS') {
                throw new Error(addResult.message || '添加到目标包失败');
            }

            showSnackbar(`成功转移 ${selectedUsers.size} 个用户到 "${targetPackage.name}"`, 'success');

            // 关闭对话框并清空选择
            setTransferDialogOpen(false);
            setSelectedUsers(new Set());
            setSelectedCards(new Set());

            // 刷新数据
            setTimeout(() => {
                loadInitialData(true);
            }, 500);

        } catch (error) {
            console.error('转移用户失败:', error);
            showSnackbar(`转移用户失败: ${error.message}`, 'error');
        } finally {
            setIsTransferring(false);
        }
    };

    // 处理删除选中用户
    const handleDeleteSelectedUsers = async () => {
        if (selectedUsers.size === 0) {
            showSnackbar('请先选择要删除的用户', 'warning');
            return;
        }

        const group = getCurrentGroup();
        const currentPackage = group?.packages[selectedPackage];

        if (!currentPackage) {
            showSnackbar('当前主播包信息错误', 'error');
            return;
        }

        const confirmed = window.confirm(
            `确定要从主播包 "${currentPackage.name}" 中删除 ${selectedUsers.size} 个用户吗？`
        );

        if (!confirmed) return;

        setIsUpdatingPackageUsers(true);

        try {
            const response = await fetch('/api/admin/game/update-users-to-streampackage', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    packageId: currentPackage.id,
                    userIds: Array.from(selectedUsers),
                    operation: 'remove'
                })
            });

            const result = await response.json();

            if (response.ok && result.code === 'SUCCESS') {
                const { summary } = result.data;

                showSnackbar(`成功删除 ${summary.removedCount} 个用户`, 'success');

                // 清空选择
                setSelectedUsers(new Set());
                setSelectedCards(new Set());

                // 刷新数据
                setTimeout(() => {
                    loadInitialData(true);
                }, 500);

            } else {
                throw new Error(result.message || '删除失败');
            }
        } catch (error) {
            console.error('删除用户失败:', error);
            showSnackbar(`删除用户失败: ${error.message}`, 'error');
        } finally {
            setIsUpdatingPackageUsers(false);
        }
    };

    // 渲染用户转移对话框
    const renderTransferDialog = () => (
        <Dialog
            open={transferDialogOpen}
            onClose={() => setTransferDialogOpen(false)}
            fullWidth
            maxWidth="sm"
            className={classes.autoGenerateDialog}
        >
            <DialogTitle className={classes.dialogTitle}>
                转移用户到其他主播包
                <IconButton
                    aria-label="close"
                    onClick={() => setTransferDialogOpen(false)}
                    style={{ position: 'absolute', right: 8, top: 8 }}
                    disabled={isTransferring}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                <Box mb={3}>
                    <Typography variant="h6" style={{ color: '#64b5f6', marginBottom: 8 }}>
                        转移信息
                    </Typography>
                    <Typography variant="body2" style={{ color: '#b0b0b0', marginBottom: 8 }}>
                        游戏分组：<span style={{ color: '#e0e0e0' }}>{getCurrentGroup()?.name || getCurrentGroup()?.gameName}</span>
                    </Typography>
                    <Typography variant="body2" style={{ color: '#b0b0b0', marginBottom: 8 }}>
                        当前主播包：<span style={{ color: '#e0e0e0' }}>{getCurrentGroup()?.packages[selectedPackage]?.name}</span>
                    </Typography>
                    <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                        选中用户数：<span style={{ color: '#64b5f6' }}>{selectedUsers.size}</span>
                    </Typography>
                </Box>

                <FormControl fullWidth variant="outlined" className={classes.criteriaField}>
                    <InputLabel>选择目标主播包</InputLabel>
                    <Select
                        value={targetPackageId}
                        onChange={(e) => setTargetPackageId(e.target.value)}
                        label="选择目标主播包"
                        disabled={isTransferring}
                    >
                        {getAvailablePackagesForTransfer().map(pkg => (
                            <MenuItem key={pkg.id} value={pkg.id}>
                                {pkg.name} ({pkg.streamerCount || pkg.streamers?.length || 0} 个主播)
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box mt={2} p={2} style={{ backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: 8 }}>
                    <Typography variant="caption" style={{ color: '#ff9800', display: 'block', marginBottom: 8 }}>
                        <InfoIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                        转移说明:
                    </Typography>
                    <Typography variant="caption" style={{ color: '#b0b0b0', display: 'block', lineHeight: 1.4 }}>
                        • 用户将从当前主播包中移除，并添加到目标主播包<br />
                        • 转移操作不可撤销，请确认选择正确的目标包<br />
                        • 转移完成后会自动刷新数据
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                <Button
                    onClick={() => setTransferDialogOpen(false)}
                    color="default"
                    variant="outlined"
                    disabled={isTransferring}
                >
                    取消
                </Button>
                <Button
                    onClick={handleExecuteTransfer}
                    color="primary"
                    variant="contained"
                    disabled={isTransferring || !targetPackageId}
                    startIcon={<SendIcon />}
                >
                    {isTransferring ? '转移中...' : '确认转移'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // 修复主播包详情页的渲染逻辑
    const renderPackageStreamers = () => {
        const group = getCurrentGroup();
        if (!group) {
            return (
                <div className={classes.emptyState}>
                    <InfoIcon />
                    <Typography variant="h6">{getText('pleaseSelectGroupAndPackage')}</Typography>
                    <Typography variant="body2">
                        {getText('selectGroupAndPackageToView')}
                    </Typography>
                </div>
            );
        }

        const currentPackage = group.packages[selectedPackage];
        if (!currentPackage) {
            return (
                <div className={classes.emptyState}>
                    <InfoIcon />
                    <Typography variant="h6">{getText('packageNotExists')}</Typography>
                    <Typography variant="body2">
                        {getText('currentSelectedPackageInvalid')}
                    </Typography>
                </div>
            );
        }

        // 获取当前主播包的主播列表
        const currentStreamers = currentPackage.users || currentPackage.streamers || [];
        const availablePackages = getAvailablePackagesForTransfer();

        if (currentStreamers.length === 0) {
            return (
                <div className={classes.emptyState}>
                    <PersonIcon />
                    <Typography variant="h6">{getText('noStreamers')}</Typography>
                    <Typography variant="body2">
                        {getText('noStreamersInPackage')}
                    </Typography>
                </div>
            );
        }

        return (
            <>
                {/* 操作栏 */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" style={{ color: '#e0e0e0' }}>
                        主播列表 ({currentStreamers.length} 个主播)
                        {selectedUsers.size > 0 && (
                            <span style={{ color: '#64b5f6', marginLeft: 8 }}>
                                - 已选择 {selectedUsers.size} 个
                            </span>
                        )}
                    </Typography>

                    {/* 批量操作按钮 */}
                    <Box className={classes.bulkActions}>
                        <Button
                            className={`${classes.bulkActionBtn} ${classes.selectAllBtn}`}
                            onClick={handleSelectAllCards}
                            startIcon={<CheckBoxIcon />}
                            disabled={currentStreamers.length === 0}
                        >
                            {getText('oneClickSelect')}
                        </Button>
                    </Box>
                </Box>

                {/* 选中用户操作栏 */}
                {selectedUsers.size > 0 && (
                    <Box
                        mb={2}
                        p={2}
                        style={{
                            backgroundColor: '#29314a',
                            borderRadius: 8,
                            border: '1px solid rgba(138, 43, 226, 0.4)'
                        }}
                    >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="body1" style={{ color: '#8A2BE2' }}>
                                <CheckBoxIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                                {getText('selectedStreamers', { count: selectedUsers.size }, `已选择 ${selectedUsers.size} 个主播`)}
                            </Typography>

                            <Box className={classes.selectedUserActions}>
                                {/* 转移到其他包按钮 */}
                                <Button
                                    className={`${classes.bulkActionBtn}`}
                                    onClick={handleTransferUsers}
                                    startIcon={<SendIcon />}
                                    disabled={selectedUsers.size === 0 || availablePackages.length === 0 || isUpdatingPackageUsers}
                                    style={{
                                        backgroundColor: '#4caf50',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: '#388e3c',
                                        },
                                    }}
                                    size="small"
                                >
                                    {getText('transferToOtherPackage', { count: availablePackages.length })}
                                </Button>

                                {/* 删除用户按钮 */}
                                <Button
                                    className={`${classes.bulkActionBtn}`}
                                    onClick={handleDeleteSelectedUsers}
                                    startIcon={<DeleteIcon />}
                                    disabled={selectedUsers.size === 0 || isUpdatingPackageUsers}
                                    style={{
                                        backgroundColor: '#f44336',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: '#d32f2f',
                                        },
                                    }}
                                    size="small"
                                >
                                    {isUpdatingPackageUsers ? getText('deleting') : getText('deleteSelected', { count: selectedUsers.size })}
                                </Button>

                                {/* 清除选择按钮 */}
                                <Button
                                    className={`${classes.bulkActionBtn} ${classes.deselectAllBtn}`}
                                    onClick={handleDeselectAllCards}
                                    startIcon={<ClearIcon />}
                                    disabled={selectedUsers.size === 0}
                                    size="small"
                                >
                                    {getText('clearSelection')}
                                </Button>
                            </Box>
                        </Box>

                        <Box mt={1}>
                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                {getText('selectedStreamers')}
                                {currentStreamers
                                    .filter(user => selectedUsers.has(user.id))
                                    .map(user => user.username || user.displayName || '未知用户')
                                    .slice(0, 5)
                                    .join('、')}
                                {selectedUsers.size > 5 && ` 等${selectedUsers.size}个主播`}
                            </Typography>

                            {availablePackages.length === 0 && (
                                <Typography variant="caption" style={{ color: '#ff7043', display: 'block', marginTop: 4 }}>
                                    {getText('selectGroupAndPackageToView')}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {/* 主播卡片网格 */}
                <div className={classes.cardContainer}>
                    {currentStreamers.map(user => {
                        // 确保用户对象有效再渲染
                        if (!user || !user.id) {
                            console.warn('跳过无效用户:', user);
                            return null;
                        }
                        return renderStreamerCard(user);
                    }).filter(Boolean)} {/* 过滤掉null值 */}
                </div>
            </>
        );
    };

    // 修改获取游戏背景图片的函数
    const getGameBackgroundImage = (game) => {
        // 按优先级获取图片：cover -> coverFeatured -> logo -> 默认
        if (game && typeof game === 'object') {
            if (game.cover) {
                return game.cover;
            }
            if (game.coverFeatured) {
                return game.coverFeatured;
            }
            if (game.logo) {
                return game.logo;
            }
        }
        return null;
    };

    // 修改renderHomePage函数中的Grid间距
    const renderHomePage = () => (
        <div className={classes.homeContainer}>
            <Typography variant="h4" className={classes.welcomeTitle}>
                <GroupIcon style={{ verticalAlign: 'middle', marginRight: 16, fontSize: '2rem' }} />
                {getText('streamerGroupManagement')}
            </Typography>

            {/* 统计信息保持不变 */}
            {realTimeData.statistics && (
                <Paper className={classes.systemStatsContainer}>
                    <Typography variant="h6" style={{ color: '#e0e0e0', marginBottom: 24 }}>
                        {getText('groupList')}
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={6} md={3} lg={3}>
                            <Box textAlign="center">
                                <Typography variant="h4" style={{ color: '#64b5f6', fontWeight: 700, marginBottom: 8 }}>
                                    {realTimeData.statistics.totalGames}
                                </Typography>
                                <Typography variant="body1" style={{ color: '#b0b0b0', fontWeight: 500 }}>
                                    {getText('totalGames')}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={6} md={3} lg={3}>
                            <Box textAlign="center">
                                <Typography variant="h4" style={{ color: '#64b5f6', fontWeight: 700, marginBottom: 8 }}>
                                    {realTimeData.statistics.totalGroups}
                                </Typography>
                                <Typography variant="body1" style={{ color: '#b0b0b0', fontWeight: 500 }}>
                                    {getText('totalGroups')}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={6} md={3} lg={3}>
                            <Box textAlign="center">
                                <Typography variant="h4" style={{ color: '#64b5f6', fontWeight: 700, marginBottom: 8 }}>
                                    {realTimeData.statistics.totalPackages}
                                </Typography>
                                <Typography variant="body1" style={{ color: '#b0b0b0', fontWeight: 500 }}>
                                    {getText('totalPackages')}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={6} md={3} lg={3}>
                            <Box textAlign="center">
                                <Typography variant="h4" style={{ color: '#64b5f6', fontWeight: 700, marginBottom: 8 }}>
                                    {realTimeData.statistics.totalUsers}
                                </Typography>
                                <Typography variant="body1" style={{ color: '#b0b0b0', fontWeight: 500 }}>
                                    {getText('totalStreamers')}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* 调整后的分组卡片 - 更高的卡片和更大的间距 */}
            <div className={classes.existingGroupsContainer}>
                <Typography variant="h6" style={{ color: '#e0e0e0', marginBottom: 32, textAlign: 'left' }}>
                    {getText('existingGroups', { count: gameGroups.length + customGroups.length })}
                </Typography>

                {/* 增加网格间距 */}
                <Grid container spacing={3.5}> {/* 增加间距从2.5到3.5 */}
                    {/* 游戏默认分组 - 更高的卡片 */}
                    {gameGroups.map(group => {
                        const backgroundImage = getGameBackgroundImage(group.gameData);

                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={group.id}>
                                <Card
                                    className={classes.groupCard}
                                    onClick={() => handleGroupSelect(group.id)}
                                >
                                    {/* 背景图片层 */}
                                    {backgroundImage ? (
                                        <div
                                            className={classes.gameCardBackground}
                                            style={{
                                                backgroundImage: `url(${backgroundImage})`
                                            }}
                                        />
                                    ) : (
                                        <div className={classes.gameCardDefaultBackground} />
                                    )}

                                    {/* 内容遮罩层 */}
                                    <div className={classes.gameCardOverlay} />

                                    {/* 悬停效果叠加层 */}
                                    <div className={classes.hoverOverlay} />

                                    <div className={classes.groupCardContent}>
                                        {/* 游戏名称 */}
                                        <Typography className={classes.gameTitle}>
                                            {group.gameName}
                                        </Typography>

                                        {/* 统计信息 */}
                                        <div className={classes.gameStatsContainer}>
                                            <div className={classes.statItem}>
                                                <span className={classes.statNumber}>
                                                    {group.packages.length}
                                                </span>
                                                <span className={classes.statLabel}>{getText('packages')}</span>
                                            </div>
                                            <div className={classes.statItem}>
                                                <span className={classes.statNumber}>
                                                    {group.totalStreamers}
                                                </span>
                                                <span className={classes.statLabel}>{getText('streamers')}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </Grid>
                        );
                    })}

                    {/* 自定义分组 - 更高的卡片 */}
                    {customGroups.map(group => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={group.id}>
                            <Card
                                className={classes.groupCard}
                                onClick={() => handleGroupSelect(group.id)}
                            >
                                {/* 自定义分组使用默认渐变背景 */}
                                <div
                                    className={classes.gameCardDefaultBackground}
                                    style={{
                                        background: 'linear-gradient(135deg, #6A0DAD, #8e24aa, #9c27b0)'
                                    }}
                                />

                                {/* 自定义分组使用特殊遮罩 */}
                                <div className={classes.customGroupOverlay} />

                                {/* 悬停效果叠加层 */}
                                <div className={classes.hoverOverlay} />

                                <div className={classes.groupCardContent}>
                                    {/* 分组名称 */}
                                    <Typography className={classes.gameTitle}>
                                        {group.name || group.groupName}
                                    </Typography>

                                    {/* 统计信息 - 自定义样式 */}
                                    <div className={`${classes.gameStatsContainer} ${classes.customGroupStats}`}>
                                        <div className={classes.statItem}>
                                            <span className={classes.statNumber}>
                                                {group.packages.length}
                                            </span>
                                            <span className={classes.statLabel}>主播包</span>
                                        </div>
                                        <div className={classes.statItem}>
                                            <span className={classes.statNumber}>
                                                {group.totalStreamers}
                                            </span>
                                            <span className={classes.statLabel}>主播</span>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </div>
        </div>
    );

    // 处理更多操作菜单
    const handleMoreActionsClick = (event) => {
        setMoreActionsAnchor(event.currentTarget);
    };

    const handleMoreActionsClose = () => {
        setMoreActionsAnchor(null);
    };

    // 打开自动生成对话框
    const handleOpenAutoGenerate = () => {
        setMoreActionsAnchor(null);
        setAutoGenerateDialogOpen(true);
        setGenerationResult(null);
    };

    // 修改自动生成完成后的数据刷新
    const handleAutoGenerate = async () => {
        setIsGenerating(true);
        setGenerationResult(null);

        try {
            const response = await fetch('/api/v1/admin/game/generate-streamer-package', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const result = await response.json();

            if (response.ok) {
                setGenerationResult(result);

                if (result.data.createdGroups > 0) {
                    showSnackbar(`自动生成完成：创建了 ${result.data.createdGroups} 个分组和 ${result.data.createdPackages} 个主播包`, 'success');

                    // 重新加载数据以获取最新的分组信息
                    setTimeout(() => {
                        loadInitialData();
                    }, 500);
                } else {
                    showSnackbar(result.message || '没有需要创建的新分组或主播包', 'info');
                }
            } else {
                throw new Error(result.message || '自动生成失败');
            }
        } catch (error) {
            console.error('自动生成失败:', error);
            showSnackbar(`自动生成失败: ${error.message}`, 'error');
        } finally {
            setIsGenerating(false);
        }
    };
    //=============================添加发送邀约功能========================

    // 在现有状态中添加任务邀约相关状态
    const [taskInvitationDialogOpen, setTaskInvitationDialogOpen] = useState(false);
    const [activeQuests, setActiveQuests] = useState([]);
    const [selectedQuests, setSelectedQuests] = useState([]);
    const [isLoadingQuests, setIsLoadingQuests] = useState(false);
    const [isSendingInvitation, setIsSendingInvitation] = useState(false);

    // 获取活跃任务的函数
    const fetchActiveQuests = async (gameId) => {
        setIsLoadingQuests(true);
        try {
            const response = await fetch(`/api/v1/admin/game/${gameId}/get-active-quests`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (response.ok) {
                const result = await response.json();
                if (result.code === 'GAME_FOUND' && result.data) {
                    setActiveQuests(result.data);
                } else {
                    setActiveQuests([]);
                }
            } else {
                throw new Error('获取活跃任务失败');
            }
        } catch (error) {
            console.error('获取活跃任务失败:', error);
            showSnackbar('获取活跃任务失败: ' + error.message, 'error');
            setActiveQuests([]);
        } finally {
            setIsLoadingQuests(false);
        }
    };

    // 打开任务邀约弹窗
    const handleOpenTaskInvitationDialog = async () => {
        const group = getCurrentGroup();
        if (!group || !group.gameId) {
            showSnackbar('当前分组信息不完整，无法获取游戏ID', 'error');
            return;
        }

        if (selectedUsers.size === 0) {
            showSnackbar('请先选择要发送邀约的主播', 'warning');
            return;
        }

        setTaskInvitationDialogOpen(true);
        await fetchActiveQuests(group.gameId);
    };

    // 关闭任务邀约弹窗
    const handleCloseTaskInvitationDialog = () => {
        setTaskInvitationDialogOpen(false);
        setSelectedQuests([]);
        setActiveQuests([]);
    };

    // 处理任务选择
    const handleQuestSelection = (questId) => {
        setSelectedQuests(prev => {
            if (prev.includes(questId)) {
                return prev.filter(id => id !== questId);
            } else {
                return [...prev, questId];
            }
        });
    };

    // 全选/取消全选任务
    const handleSelectAllQuests = () => {
        if (selectedQuests.length === activeQuests.length) {
            setSelectedQuests([]);
        } else {
            setSelectedQuests(activeQuests.map(quest => quest.id));
        }
    };

    // 发送任务邀约邮件
    const handleSendTaskInvitation = async () => {
        if (selectedQuests.length === 0) {
            showSnackbar('请至少选择一个任务', 'warning');
            return;
        }

        if (selectedUsers.size === 0) {
            showSnackbar('请至少选择一个用户', 'warning');
            return;
        }

        setIsSendingInvitation(true);

        try {
            const group = getCurrentGroup();
            // const currentStreamers = getCurrentStreamers();

            // 构建任务数据 - 修改：使用 title 字段
            const tasks = selectedQuests.map(questId => {
                const quest = activeQuests.find(q => q.id === questId);
                return {
                    id: quest.id,
                    name: quest.title || quest.name || '未命名任务', // 优先使用 title
                    link: `${window.location.origin}/quests/${quest.id}`
                };
            });

            // 获取游戏名称
            const gameName = group?.gameName || group?.name || '未知游戏';

            // 构建主任务链接
            const primaryTaskLink = `${window.location.origin}/game/${group.gameId}`;

            let successCount = 0;
            let failureCount = 0;
            const errors = [];

            // 为每个选中的用户发送邮件
            for (const userId of selectedUsers) {
                try {
                    const response = await fetch('/api/admin/quest/send-user-invitation-email', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            userId: userId,
                            tasks: tasks,
                            gameName: gameName,
                            primaryTaskLink: primaryTaskLink
                        })
                    });

                    const result = await response.json();
                    if (response.ok) {
                        successCount++;
                    } else {
                        failureCount++;
                        errors.push(`用户 ${userId}: ${result.error || '发送失败'}`);
                    }
                } catch (error) {
                    failureCount++;
                    errors.push(`用户 ${userId}: ${error.message}`);
                }
            }

            // 显示结果
            if (successCount > 0 && failureCount === 0) {
                showSnackbar(`成功发送 ${successCount} 封任务邀约邮件`, 'success');
            } else if (successCount > 0 && failureCount > 0) {
                showSnackbar(`发送完成：成功 ${successCount} 封，失败 ${failureCount} 封`, 'warning');
            } else {
                showSnackbar(`发送失败：${failureCount} 封邮件发送失败`, 'error');
            }

            // 关闭弹窗
            handleCloseTaskInvitationDialog();

        } catch (error) {
            console.error('发送任务邀约失败:', error);
            showSnackbar('发送任务邀约失败: ' + error.message, 'error');
        } finally {
            setIsSendingInvitation(false);
        }
    };

    // 获取选中用户的详细信息
    const getSelectedUsersDetails = () => {
        const currentStreamers = getCurrentStreamers();
        return currentStreamers.filter(user => selectedUsers.has(user.id));
    };

    // 修改任务邀约弹窗中的任务显示部分
    const renderTaskInvitationDialog = () => {
        const selectedUsersDetails = getSelectedUsersDetails();

        // 格式化日期时间
        const formatDateTime = (dateString) => {
            if (!dateString) return '未设置';
            const date = new Date(dateString);
            return date.toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
            });
        };

        // 格式化时长
        const formatDuration = (hours) => {
            if (!hours || hours === 0) return '无要求';
            if (hours < 1) {
                return `${Math.round(hours * 60)}分钟`;
            } else if (hours >= 24) {
                const days = Math.floor(hours / 24);
                const remainingHours = hours % 24;
                return remainingHours > 0 ? `${days}天${remainingHours}小时` : `${days}天`;
            } else {
                return `${hours}小时`;
            }
        };

        // 获取任务类型标签
        const getQuestTypeInfo = (type) => {
            const typeMap = {
                'streaming': { label: '直播任务', className: 'type-streaming' },
                'secondary-creation': { label: '二创任务', className: 'type-secondary-creation' },
                'content-creation': { label: '内容创作', className: 'type-secondary-creation' },
                'promotion': { label: '推广任务', className: 'type-default' },
                'event': { label: '活动任务', className: 'type-default' },
            };
            return typeMap[type] || { label: type || '未知类型', className: 'type-default' };
        };

        // 检查任务是否紧急（24小时内结束）
        const isUrgent = (endDateTime) => {
            if (!endDateTime) return false;
            const now = new Date();
            const end = new Date(endDateTime);
            const hoursRemaining = (end - now) / (1000 * 60 * 60);
            return hoursRemaining > 0 && hoursRemaining <= 24;
        };

        // 获取任务统计信息
        const getQuestStats = () => {
            if (activeQuests.length === 0) return null;

            const stats = {
                total: activeQuests.length,
                selected: selectedQuests.length,
                streaming: activeQuests.filter(q => q.type === 'streaming').length,
                secondaryCreation: activeQuests.filter(q => q.type === 'secondary-creation').length,
                urgent: activeQuests.filter(q => isUrgent(q.endDateTime)).length,
            };

            return stats;
        };

        const questStats = getQuestStats();

        return (
            <Dialog
                open={taskInvitationDialogOpen}
                onClose={handleCloseTaskInvitationDialog}
                fullWidth
                maxWidth="lg" // 改为更大的尺寸
                className={classes.autoGenerateDialog}
            >
                <DialogTitle className={classes.dialogTitle}>
                    发送任务邀约
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseTaskInvitationDialog}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className={classes.dialogContent}>
                    {/* 用户信息预览 */}
                    <Box mb={3} p={2} style={{ backgroundColor: '#29314a', borderRadius: 8 }}>
                        <Typography variant="body2" style={{ color: '#64b5f6', marginBottom: 8 }}>
                            <PersonIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                            即将发送给 {selectedUsers.size} 个主播：
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {selectedUsersDetails
                                .slice(0, 5)
                                .map(userDetail => (
                                    <Chip
                                        key={userDetail.id}
                                        label={userDetail.username || userDetail.displayName || '未知用户'}
                                        size="small"
                                        style={{
                                            backgroundColor: getStreamerTypeColor(determineUserStreamerType(userDetail)),
                                            color: 'white',
                                            fontSize: '11px'
                                        }}
                                    />
                                ))}
                            {selectedUsers.size > 5 && (
                                <Chip
                                    label={`+${selectedUsers.size - 5} 更多`}
                                    size="small"
                                    style={{
                                        backgroundColor: '#757575',
                                        color: 'white',
                                        fontSize: '11px'
                                    }}
                                />
                            )}
                        </Box>
                    </Box>

                    {/* 任务选择区域 */}
                    <Box mb={2}>
                        {/* 任务选择标题和统计 */}
                        <Box className={classes.questSelectAll}>
                            <Box>
                                <Typography variant="h6" style={{ color: '#e0e0e0', marginBottom: 4 }}>
                                    选择要发送的任务
                                </Typography>
                                {questStats && (
                                    <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                        共 {questStats.total} 个任务，已选择 {questStats.selected} 个
                                    </Typography>
                                )}
                            </Box>
                            {activeQuests.length > 0 && (
                                <Button
                                    size="small"
                                    onClick={handleSelectAllQuests}
                                    style={{ color: '#64b5f6' }}
                                    startIcon={selectedQuests.length === activeQuests.length ? <ClearIcon /> : <CheckBoxIcon />}
                                >
                                    {selectedQuests.length === activeQuests.length ? '取消全选' : '全选任务'}
                                </Button>
                            )}
                        </Box>

                        {/* 任务统计卡片 */}
                        {questStats && (
                            <Box className={classes.questStats}>
                                <Typography variant="subtitle2" style={{ color: '#64b5f6', marginBottom: 8, textAlign: 'center' }}>
                                    任务概览
                                </Typography>
                                <Box className={classes.questStatsGrid}>
                                    <Box className={classes.questStatItem}>
                                        <Typography className={classes.questStatNumber}>{questStats.total}</Typography>
                                        <Typography className={classes.questStatLabel}>总任务</Typography>
                                    </Box>
                                    <Box className={classes.questStatItem}>
                                        <Typography className={classes.questStatNumber}>{questStats.selected}</Typography>
                                        <Typography className={classes.questStatLabel}>已选择</Typography>
                                    </Box>
                                    <Box className={classes.questStatItem}>
                                        <Typography className={classes.questStatNumber}>{questStats.streaming}</Typography>
                                        <Typography className={classes.questStatLabel}>直播任务</Typography>
                                    </Box>
                                    <Box className={classes.questStatItem}>
                                        <Typography className={classes.questStatNumber}>{questStats.secondaryCreation}</Typography>
                                        <Typography className={classes.questStatLabel}>二创任务</Typography>
                                    </Box>
                                    <Box className={classes.questStatItem}>
                                        <Typography className={classes.questStatNumber} style={{ color: questStats.urgent > 0 ? '#f44336' : '#64b5f6' }}>
                                            {questStats.urgent}
                                        </Typography>
                                        <Typography className={classes.questStatLabel}>紧急任务</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* 任务列表 */}
                        {isLoadingQuests ? (
                            <Box className={classes.questLoadingContainer}>
                                <CircularProgress size={40} style={{ color: '#64b5f6' }} />
                                <Typography style={{ color: '#b0b0b0' }}>正在加载活跃任务...</Typography>
                            </Box>
                        ) : activeQuests.length === 0 ? (
                            <Box className={classes.questEmptyState}>
                                <GamepadIcon className={classes.questEmptyIcon} />
                                <Typography variant="h6" style={{ color: '#e0e0e0', marginBottom: 8 }}>
                                    暂无活跃任务
                                </Typography>
                                <Typography style={{ color: '#b0b0b0' }}>
                                    当前游戏没有可用的活跃任务
                                </Typography>
                            </Box>
                        ) : (
                            <Box className={classes.questSelectionContainer}>
                                {activeQuests.map((quest) => {
                                    const isSelected = selectedQuests.includes(quest.id);
                                    const typeInfo = getQuestTypeInfo(quest.type);
                                    const urgent = isUrgent(quest.endDateTime);

                                    return (
                                        <Box
                                            key={quest.id}
                                            className={`${classes.questCard} ${isSelected ? 'selected' : ''}`}
                                            onClick={() => handleQuestSelection(quest.id)}
                                        >
                                            {/* 任务头部 */}
                                            <Box className={classes.questHeader}>
                                                <Box className={classes.questTitleContainer}>
                                                    <Typography className={classes.questTitle}>
                                                        {quest.title || quest.name || '未命名任务'}
                                                    </Typography>
                                                    <Typography className={classes.questId}>
                                                        ID: {quest.id}
                                                    </Typography>
                                                </Box>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => handleQuestSelection(quest.id)}
                                                    className={classes.questCheckbox}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </Box>

                                            {/* 任务信息网格 */}
                                            <Box className={classes.questInfo}>
                                                <Box className={classes.questInfoItem}>
                                                    <Typography className={classes.questInfoLabel}>开始时间</Typography>
                                                    <Typography className={classes.questInfoValue}>
                                                        {formatDateTime(quest.startDateTime)}
                                                    </Typography>
                                                </Box>
                                                <Box className={classes.questInfoItem}>
                                                    <Typography className={classes.questInfoLabel}>结束时间</Typography>
                                                    <Typography
                                                        className={classes.questInfoValue}
                                                        style={{ color: urgent ? '#f44336' : '#e0e0e0' }}
                                                    >
                                                        {formatDateTime(quest.endDateTime)}
                                                    </Typography>
                                                </Box>
                                                <Box className={classes.questInfoItem}>
                                                    <Typography className={classes.questInfoLabel}>任务类型</Typography>
                                                    <Typography className={classes.questInfoValue}>
                                                        {typeInfo.label}
                                                    </Typography>
                                                </Box>
                                                <Box className={classes.questInfoItem}>
                                                    <Typography className={classes.questInfoLabel}>直播时长要求</Typography>
                                                    <Typography className={classes.questInfoValue}>
                                                        {formatDuration(quest.requiredStreamTimeHours)}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* 任务标签 */}
                                            <Box className={classes.questBadges}>
                                                <Chip
                                                    size="small"
                                                    label={quest.status || 'active'}
                                                    className={`${classes.questBadge} status-${quest.status || 'active'}`}
                                                />
                                                <Chip
                                                    size="small"
                                                    label={typeInfo.label}
                                                    className={`${classes.questBadge} ${typeInfo.className}`}
                                                />
                                                {urgent && (
                                                    <Chip
                                                        size="small"
                                                        label="紧急"
                                                        className={`${classes.questBadge} urgent`}
                                                    />
                                                )}
                                                {quest.minimumAverageViewers > 0 && (
                                                    <Chip
                                                        size="small"
                                                        label={`最低观众: ${quest.minimumAverageViewers}`}
                                                        style={{
                                                            backgroundColor: '#e91e63',
                                                            color: 'white',
                                                            fontSize: '10px',
                                                            height: '20px',
                                                        }}
                                                    />
                                                )}
                                            </Box>

                                            {/* 任务描述 */}
                                            {quest.description && (
                                                <Typography className={classes.questDescription}>
                                                    {quest.description}
                                                </Typography>
                                            )}

                                            {/* 奖励信息 */}
                                            {(quest.paymentAmount > 0 || quest.noizPoints > 0) && (
                                                <Box className={classes.questRewards}>
                                                    <StarIcon className={classes.questRewardIcon} />
                                                    <Typography className={classes.questRewardText}>
                                                        奖励:
                                                        {quest.paymentAmount > 0 && ` $${quest.paymentAmount}`}
                                                        {quest.noizPoints > 0 && ` ${quest.noizPoints} NOIZ积分`}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>

                    {/* 发送预览 */}
                    {selectedQuests.length > 0 && (
                        <Box p={2} style={{ backgroundColor: '#29314a', borderRadius: 8 }}>
                            <Typography variant="body2" style={{ color: '#64b5f6', marginBottom: 8 }}>
                                <SendIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                发送预览：
                            </Typography>
                            <Typography variant="body2" style={{ color: '#e0e0e0', marginBottom: 8 }}>
                                将向 {selectedUsers.size} 个主播发送包含 {selectedQuests.length} 个任务的邀约邮件
                            </Typography>

                            <Typography variant="caption" style={{ color: '#b0b0b0', display: 'block' }}>
                                选中的任务：
                            </Typography>
                            <Box mt={0.5} display="flex" flexWrap="wrap" gap={1}>
                                {selectedQuests.map(questId => {
                                    const quest = activeQuests.find(q => q.id === questId);
                                    return quest ? (
                                        <Chip
                                            key={questId}
                                            label={quest.title || quest.name || '未命名任务'}
                                            size="small"
                                            style={{
                                                backgroundColor: '#1976d2',
                                                color: 'white',
                                                fontSize: '11px',
                                            }}
                                        />
                                    ) : null;
                                })}
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={handleCloseTaskInvitationDialog}
                        color="default"
                        variant="outlined"
                        disabled={isSendingInvitation}
                    >
                        取消
                    </Button>
                    <Button
                        onClick={handleSendTaskInvitation}
                        color="primary"
                        variant="contained"
                        disabled={selectedQuests.length === 0 || selectedUsers.size === 0 || isSendingInvitation}
                        startIcon={isSendingInvitation ? <CircularProgress size={16} /> : <SendIcon />}
                        style={{
                            backgroundColor: '#6A0DAD',
                            color: 'white'
                        }}
                    >
                        {isSendingInvitation ? '发送中...' : `发送邀约 (${selectedUsers.size} 个主播)`}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    //=============================End添加发送邀约功能========================

    // 修复左侧栏中主播数量显示的国际化
    const renderSidebarGroupItem = (group) => (
        <ListItem
            key={group.id}
            className={`${classes.groupListItem} ${selectedGroupId === group.id ? 'selected' : ''}`}
            onClick={() => handleGroupSelect(group.id)}
        >
            <Box display="flex" alignItems="center" width="100%">
                {group.type === 'game' ?
                    <GamepadIcon className={classes.gameIcon} /> :
                    <GroupIcon className={classes.gameIcon} />
                }
                <Box flex={1}>
                    <Typography className={classes.groupName}>
                        {group.name || group.gameName}
                    </Typography>
                    <Typography className={classes.groupCount}>
                        {group.totalStreamers === 1
                            ? getText('streamerCount', { count: group.totalStreamers || 0 })
                            : getText('streamersCount', { count: group.totalStreamers || 0 })
                        }
                    </Typography>
                </Box>
            </Box>
        </ListItem>
    );

    // 关闭自动生成对话框
    const handleCloseAutoGenerate = () => {
        setAutoGenerateDialogOpen(false);
        setGenerationResult(null);
        setIsGenerating(false);
    };

    // 修复渲染分组详情页函数中的主播列表部分
    const renderGroupDetailPage = () => {
        const group = getCurrentGroup();
        if (!group) return null;

        const currentStreamers = getCurrentStreamers();
        const currentPackageName = group.packages[selectedPackage]?.name || '默认包';
        const daysOptions = [30, 60, 90, 120, 365];

        return (
            <div>

                {/* 包操作按钮行 */}
                <Box className={classes.packageActionsRow}>
                    <Box className={classes.packageActionsLeft}>
                        <Button
                            className={`${classes.actionButton} view`}
                            startIcon={<VisibilityIcon />}
                            onClick={handleViewExpectedData}
                            disabled={isLoadingPackageUsers}
                        >
                            {getText('viewExpectedData')}
                        </Button>
                        <Button
                            className={`${classes.bulkActionBtn}`}
                            onClick={handleSendInvitation}
                            startIcon={<SendIcon />}
                            disabled={selectedUsers.size === 0}
                            style={{
                                backgroundColor: '#ff7043',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: '#f4511e',
                                },
                            }}
                            size="small"
                        >
                            {getText('sendTaskInvitation')} ({selectedUsers.size})
                        </Button>
                    </Box>

                    <Box className={classes.packageActionsRight}>
                        <Box className={classes.daysSelector}>
                            <Typography className={classes.daysLabel}>{getText('viewDays')}</Typography>
                            {daysOptions.map(days => (
                                <Button
                                    key={days}
                                    className={`${classes.daysButton} ${selectedDays === days ? 'selected' : ''}`}
                                    onClick={() => handleDaysChange(days)}
                                    disabled={isLoadingPackageUsers}
                                >
                                    {days}{getText('days')}
                                </Button>
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* 包切换标签和管理操作 */}
                {renderPackageTabsWithActions(group)}

                {/* 主播列表标题 */}
                <Typography variant="h6" style={{ color: '#e0e0e0', marginBottom: 16 }}>
                    {currentPackageName} - {getText('streamerList', { count: currentStreamers.length })}
                    {isLoadingPackageUsers && (
                        <CircularProgress size={16} style={{ marginLeft: 8, color: '#64b5f6' }} />
                    )}
                    <Typography
                        component="span"
                        variant="caption"
                        style={{ color: '#64b5f6', marginLeft: 16, fontSize: '12px' }}
                    >
                        ({getText('recentDaysData', { days: selectedDays }, `近${selectedDays}天数据`)})
                    </Typography>
                </Typography>

                {/* 主播列表内容 */}
                {isPackageChanging ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress size={40} style={{ color: '#64b5f6' }} />
                        <Typography style={{ color: '#b0b0b0', marginLeft: 16 }}>
                            {getText('switchingToPackage', { name: currentPackageName })}
                        </Typography>
                    </Box>
                ) : isLoadingPackageUsers ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress size={40} style={{ color: '#64b5f6' }} />
                        <Typography style={{ color: '#b0b0b0', marginLeft: 16 }}>
                            {getText('loadingStreamerData')}
                        </Typography>
                    </Box>
                ) : (
                    renderPackageStreamers()
                )}
            </div>
        );
    };

    // 确保在数据更新时清除无效的选择
    useEffect(() => {
        const currentStreamers = getCurrentStreamers();
        const validUserIds = new Set(currentStreamers.map(user => user.id).filter(Boolean));

        // 清除无效的选择
        setSelectedCards(prev => new Set([...prev].filter(id => validUserIds.has(id))));
        setSelectedUsers(prev => new Set([...prev].filter(id => validUserIds.has(id))));
    }, [selectedGroupId, selectedPackage]);

    // 添加一个useEffect来处理snackbar的自动关闭
    useEffect(() => {
        if (snackbar.open) {
            const timer = setTimeout(() => {
                setSnackbar(prev => ({ ...prev, open: false }));
            }, 4000);

            return () => clearTimeout(timer);
        }
    }, [snackbar.open]);

    // 添加一个 useEffect 来监听天数变化并重新加载数据
    useEffect(() => {
        if (!isInitialLoading && selectedGroupId) {
            // 当天数改变且不是初始加载时，重新加载当前分组的数据
            const timer = setTimeout(() => {
                loadInitialData(true); // 保持当前选择状态
            }, 300); // 短暂延迟避免频繁请求

            return () => clearTimeout(timer);
        }
    }, [selectedDays]); // 监听天数变化

    // 筛选后的分组列表
    const getFilteredGroups = () => {
        const allGroups = [
            ...gameGroups.map(g => ({ ...g, type: 'game' })),
            ...customGroups.map(g => ({ ...g, type: 'custom' }))
        ];

        if (!searchQuery.trim()) return allGroups;

        return allGroups.filter(group =>
            (group.name || group.gameName).toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    // 修改主组件的返回逻辑，在初始加载时显示加载页面
    if (isInitialLoading) {
        return renderPageLoading();
    }

    return (
        <div className={classes.root}>
            {/* 左侧边栏 */}
            <div className={classes.sidebar}>
                <div className={classes.sidebarHeader}>
                    <div className={classes.searchContainer}>
                        <TextField
                            placeholder={getText('searchGroups')}
                            variant="outlined"
                            size="small"
                            className={classes.searchField}
                            value={searchQuery}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSearchQuery(value);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ color: '#b0b0b0', fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            className={classes.homeButton}
                            onClick={handleBackToHome}
                        >
                            <HomeIcon fontSize="small" />
                        </Button>
                        <Button
                            className={classes.moreActionsButton}
                            onClick={handleMoreActionsClick}
                        >
                            <MoreVertIcon fontSize="small" />
                        </Button>
                    </div>
                </div>

                {/* 更多操作菜单 */}
                <Menu
                    anchorEl={moreActionsAnchor}
                    open={Boolean(moreActionsAnchor)}
                    onClose={handleMoreActionsClose}
                    classes={{ paper: classes.menuPaper }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                >
                    <MenuItem
                        onClick={handleOpenAutoGenerate}
                        className={classes.autoGenerateMenuItem}
                    >
                        <BuildIcon style={{ marginRight: 8, fontSize: 18 }} />
                        {getText('autoGenerateDefaultGroups')}
                    </MenuItem>
                </Menu>

                {/* 分组列表 */}
                <List className={classes.groupsList}>
                    {getFilteredGroups().length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <Typography variant="body2" style={{ color: '#b0b0b0' }}>
                                {searchQuery ? getText('noMatchingGroups') : getText('noGroupData')}
                            </Typography>
                        </Box>
                    ) : (
                        getFilteredGroups().map(group => renderSidebarGroupItem(group))
                    )}
                </List>
            </div>

            {/* 右侧内容区域 */}
            <div className={classes.content}>
                <div className={classes.contentHeader}>
                    <Typography className={classes.contentTitle}>
                        {currentView === 'home' ? getText('groupManagementHome') : (getCurrentGroup()?.name || getCurrentGroup()?.gameName)}
                    </Typography>

                    {/* 在标题栏显示数据刷新状态 */}
                    {isLoadingPackageUsers && !isInitialLoading && (
                        <Box display="flex" alignItems="center">
                            <CircularProgress size={20} style={{ color: '#64b5f6', marginRight: 8 }} />
                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                {getText('refreshing')}
                            </Typography>
                        </Box>
                    )}
                </div>

                <div className={classes.contentBody}>
                    {currentView === 'home' ? renderHomePage() : renderGroupDetailPage()}
                </div>
            </div>

            {/* 自动生成对话框 */}
            <Dialog
                open={autoGenerateDialogOpen}
                onClose={handleCloseAutoGenerate}
                fullWidth
                maxWidth="sm"
                className={classes.autoGenerateDialog}
            >
                <DialogTitle className={classes.dialogTitle}>
                    {getText('autoGenerateDefaultGroupsAndPackages')}
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseAutoGenerate}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                        disabled={isGenerating}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className={classes.dialogContent}>
                    {!isGenerating && !generationResult && (
                        <Box>
                            <Typography variant="body1" style={{ marginBottom: 16 }}>
                                {getText('thisOperationWillCreate')}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#b0b0b0', marginBottom: 16 }}>
                                {getText('onlyCreateForGamesWithoutDefault')}<br />
                                {getText('onlyCreateForGroupsWithoutDefault')}<br />
                                {getText('onlyProcessActiveGamesWithTwitch')}
                            </Typography>
                            <Typography variant="body2" style={{ color: '#ff7043' }}>
                                {getText('operationCannotBeUndone')}
                            </Typography>
                        </Box>
                    )}

                    {isGenerating && (
                        <div className={classes.progressContainer}>
                            <CircularProgress size={40} style={{ color: '#64b5f6' }} />
                            <Typography className={classes.progressText}>
                                {getText('autoGeneratingGroupsAndPackages')}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#b0b0b0' }}>
                                {getText('thisCouldTakeMinutes')}
                            </Typography>
                        </div>
                    )}

                    {generationResult && (
                        <Box>
                            <Typography variant="h6" style={{ color: '#e0e0e0', marginBottom: 16 }}>
                                {getText('generationResults')}
                            </Typography>

                            <div className={classes.resultSummary}>
                                <div className={classes.resultItem}>
                                    <span>{getText('processedGames')}</span>
                                    <span className={classes.resultInfo}>{generationResult.data.processedGames}</span>
                                </div>
                                <div className={classes.resultItem}>
                                    <span>{getText('createdGroups')}</span>
                                    <span className={classes.resultSuccess}>{generationResult.data.createdGroups}</span>
                                </div>
                                <div className={classes.resultItem}>
                                    <span>{getText('createdPackages')}</span>
                                    <span className={classes.resultSuccess}>{generationResult.data.createdPackages}</span>
                                </div>
                                <div className={classes.resultItem}>
                                    <span>{getText('successfullyProcessed')}</span>
                                    <span className={classes.resultSuccess}>{generationResult.data.successfullyProcessed}</span>
                                </div>
                                <div className={classes.resultItem}>
                                    <span>{getText('failedProcessed')}</span>
                                    <span style={{ color: generationResult.data.failedProcessed > 0 ? '#e57373' : '#81c784' }}>
                                        {generationResult.data.failedProcessed}
                                    </span>
                                </div>
                            </div>

                            {generationResult.meta && (
                                <Typography variant="caption" style={{ color: '#b0b0b0', display: 'block', marginTop: 16 }}>
                                    {getText('successRate', { rate: generationResult.meta.summary.successRate })} |
                                    {getText('operator', { username: generationResult.meta.operator.username })}
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    {!isGenerating && !generationResult && (
                        <>
                            <Button onClick={handleCloseAutoGenerate} color="default" variant="outlined">
                                {getText('cancel')}
                            </Button>
                            <Button onClick={handleAutoGenerate} color="primary" variant="contained">
                                {getText('startGeneration')}
                            </Button>
                        </>
                    )}

                    {isGenerating && (
                        <Button onClick={handleCloseAutoGenerate} color="default" variant="outlined" disabled>
                            {getText('generating')}
                        </Button>
                    )}

                    {generationResult && (
                        <Button onClick={handleCloseAutoGenerate} color="primary" variant="contained">
                            {getText('completed')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* 主播包管理对话框 */}
            <Dialog
                open={packageDialogOpen}
                onClose={() => setPackageDialogOpen(false)}
                fullWidth
                maxWidth="md"
                className={classes.autoGenerateDialog}
            >
                <DialogTitle className={classes.dialogTitle}>
                    {packageDialogMode === 'create' ? getText('createPackage') : getText('editPackage')}
                    <IconButton
                        aria-label="close"
                        onClick={() => setPackageDialogOpen(false)}
                        style={{ position: 'absolute', right: 8, top: 8 }}
                        disabled={isPackageProcessing}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className={classes.dialogContent}>
                    {/* 基本信息 */}
                    <div className={classes.packageFormSection}>
                        <Typography className={classes.packageFormSectionTitle}>
                            {getText('packageBasicInfo')}
                        </Typography>
                        <TextField
                            label={getText('packageName')}
                            fullWidth
                            variant="outlined"
                            value={packageFormData.name}
                            onChange={(e) => {
                                const value = e.target.value;
                                setPackageFormData(prev => ({ ...prev, name: value || '' }));
                            }}
                            placeholder={getText('enterPackageName')}
                            className={classes.criteriaField}
                            inputProps={{ maxLength: 50 }}
                        />
                        <TextField
                            label={getText('packageDescription')}
                            fullWidth
                            variant="outlined"
                            multiline
                            minRows={2}
                            value={packageFormData.description}
                            onChange={(e) => {
                                const value = e.target.value;
                                setPackageFormData(prev => ({ ...prev, description: value || '' }));
                            }}
                            placeholder={getText('enterPackageDescription')}
                            className={classes.criteriaField}
                            inputProps={{ maxLength: 200 }}
                        />
                    </div>

                    {/* 包设置 */}
                    <div className={classes.packageFormSection}>
                        <Typography className={classes.packageFormSectionTitle}>
                            {getText('packageSettings')}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    label={getText('maxStreamers')}
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    value={packageFormData.settings.maxStreamers}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setPackageFormData(prev => ({
                                            ...prev,
                                            settings: { ...prev.settings, maxStreamers: parseInt(value) || 100 }
                                        }));
                                    }}
                                    className={classes.criteriaField}
                                    inputProps={{ min: 1, max: 500 }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label={getText('priority')}
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    value={packageFormData.settings.priority}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setPackageFormData(prev => ({
                                            ...prev,
                                            settings: { ...prev.settings, priority: parseInt(value) || 0 }
                                        }));
                                    }}
                                    className={classes.criteriaField}
                                    inputProps={{ min: 0, max: 10 }}
                                    helperText={getText('higherNumberHigherPriority')}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} style={{ marginTop: 8 }}>
                            <Grid item xs={6}>
                                <FormControl fullWidth variant="outlined" className={classes.criteriaField}>
                                    <InputLabel>{getText('visibilityLevel')}</InputLabel>
                                    <Select
                                        value={packageFormData.settings.visibilityLevel}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setPackageFormData(prev => ({
                                                ...prev,
                                                settings: { ...prev.settings, visibilityLevel: value || 'public' }
                                            }));
                                        }}
                                        label={getText('visibilityLevel')}
                                    >
                                        <MenuItem value="public">{getText('public')}</MenuItem>
                                        <MenuItem value="private">{getText('private')}</MenuItem>
                                        <MenuItem value="restricted">{getText('restricted')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label={getText('tags')}
                                    fullWidth
                                    variant="outlined"
                                    value={packageFormData.settings.tags.join(', ')}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setPackageFormData(prev => ({
                                            ...prev,
                                            settings: {
                                                ...prev.settings,
                                                tags: value ? value.split(',').map(tag => tag.trim()).filter(tag => tag) : []
                                            }
                                        }));
                                    }}
                                    placeholder={getText('enterTagsCommaSeparated')}
                                    className={classes.criteriaField}
                                    helperText={getText('tagsExample')}
                                />
                            </Grid>
                        </Grid>
                    </div>

                    {/* 主播选择（如果需要的话） */}
                    <div className={classes.packageFormSection}>
                        <Typography className={classes.packageFormSectionTitle}>
                            {getText('streamerManagement')}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#b0b0b0', marginBottom: 16 }}>
                            {getText('currentlySelected', { count: packageFormData.streamerIds.length })}
                        </Typography>
                        {packageFormData.streamerIds.length > 0 && (
                            <Box display="flex" flexWrap="wrap" gap={1}>
                                {packageFormData.streamerIds.slice(0, 10).map((id, index) => (
                                    <Chip
                                        key={index}
                                        label={`${getText('streamer')} ${id}`}
                                        size="small"
                                        style={{ backgroundColor: '#64b5f6', color: 'white' }}
                                        onDelete={() => setPackageFormData(prev => ({
                                            ...prev,
                                            streamerIds: prev.streamerIds.filter(streamerId => streamerId !== id)
                                        }))}
                                    />
                                ))}
                                {packageFormData.streamerIds.length > 10 && (
                                    <Chip
                                        label={`+${packageFormData.streamerIds.length - 10} ${getText('more')}`}
                                        size="small"
                                        style={{ backgroundColor: '#757575', color: 'white' }}
                                    />
                                )}
                            </Box>
                        )}
                    </div>

                    {/* 显示包的统计信息（编辑模式下） */}
                    {packageDialogMode === 'edit' && currentEditingPackage?.statistics && (
                        <Box marginTop={2} padding={2} style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 4 }}>
                            <Typography variant="caption" style={{ color: '#64b5f6', display: 'block', marginBottom: 8 }}>
                                包统计信息:
                            </Typography>
                            <Typography variant="caption" style={{ color: '#b0b0b0', display: 'block' }}>
                                实际主播数: {currentEditingPackage.statistics.streamers} |
                                平均观众数: {currentEditingPackage.statistics.averageCCV} |
                                总粉丝数: {currentEditingPackage.statistics.totalFollowers.toLocaleString()}
                            </Typography>
                            {currentEditingPackage.statistics.calculationPeriod && (
                                <Typography variant="caption" style={{ color: '#b0b0b0', display: 'block', marginTop: 4 }}>
                                    统计周期: {currentEditingPackage.statistics.calculationPeriod}
                                </Typography>
                            )}
                        </Box>
                    )}

                    <Typography variant="caption" style={{ color: '#b0b0b0', display: 'block', marginTop: 8 }}>
                        {getText('streamerListAutoGenerated')}
                    </Typography>
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={() => setPackageDialogOpen(false)}
                        color="default"
                        variant="outlined"
                        disabled={isPackageProcessing}
                    >
                        {getText('cancel')}
                    </Button>
                    <Button
                        onClick={handlePackageSubmit}
                        color="primary"
                        variant="contained"
                        disabled={isPackageProcessing}
                    >
                        {isPackageProcessing ? getText('processing') : (packageDialogMode === 'create' ? getText('create') : getText('update'))}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 删除确认对话框 */}
            <Dialog
                open={deleteConfirmDialog.open}
                onClose={() => setDeleteConfirmDialog({ open: false, packageId: null, packageName: '' })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle}>
                    {getText('confirmDeletePackage')}
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <Typography variant="body1" style={{ marginBottom: 16 }}>
                        {getText('confirmDeletePackageMessage', { name: deleteConfirmDialog.packageName })}
                    </Typography>
                    <Typography variant="body2" style={{ color: '#ff7043' }}>
                        {getText('cannotUndoOperation')}
                    </Typography>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={() => setDeleteConfirmDialog({ open: false, packageId: null, packageName: '' })}
                        color="default"
                        variant="outlined"
                        disabled={isPackageProcessing}
                    >
                        {getText('cancel')}
                    </Button>
                    <Button
                        onClick={handleDeletePackage}
                        className={classes.packageDeleteButton}
                        variant="contained"
                        disabled={isPackageProcessing}
                    >
                        {isPackageProcessing ? getText('deleting') : getText('confirmDelete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 添加新的转移对话框 */}
            {renderTransferDialog()}

            {/* 在所有对话框后面添加预期数据对话框 */}
            {renderExpectedDataDialog()}

            {/* 通知组件 */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000} // 减少到4秒
                onClose={(event, reason) => {
                    // 确保在所有情况下都能正确关闭
                    if (reason === 'clickaway') {
                        return;
                    }
                    setSnackbar({ ...snackbar, open: false });
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Paper
                    className={`${classes.notificationPaper} ${snackbar.severity}`}
                    onClick={() => {
                        // 点击通知时手动关闭
                        setSnackbar({ ...snackbar, open: false });
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    {snackbar.severity === 'success' && <CheckCircleIcon />}
                    {snackbar.severity === 'error' && <ErrorIcon />}
                    {snackbar.severity === 'warning' && <WarningIcon />}
                    {snackbar.severity === 'info' && <InfoIcon />}
                    <Typography>{snackbar.message}</Typography>
                    {/* 添加关闭按钮 */}
                    <IconButton
                        size="small"
                        aria-label="close"
                        color="inherit"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSnackbar({ ...snackbar, open: false });
                        }}
                        style={{ marginLeft: 'auto', padding: 4 }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Paper>
            </Snackbar>

            {renderTaskInvitationDialog()}

            {/* 添加用户管理弹窗 */}
            {/* {renderUserManagementDialog()} */}
        </div>
    );
}